// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import NotificationService from './services/NotificationService.js';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  deleteDoc,
  query,
  limit,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  addDoc
} from "firebase/firestore";
import { 
  getStorage,
  ref as storageRef, 
  uploadBytes as storageUploadBytes, 
  getDownloadURL as storageGetDownloadURL, 
  deleteObject as storageDeleteObject 
} from 'firebase/storage';


const firebaseConfig = {
  apiKey: "AIzaSyDTCte23T93ucmDqdMUq0R16A08RhbAJXg",
  authDomain: "app-ore-utenti.firebaseapp.com",
  projectId: "app-ore-utenti",
  storageBucket: "app-ore-utenti.firebasestorage.app",
  messagingSenderId: "881014422624",
  appId: "1:881014422624:web:514706d1c69b82feeb5705",
  measurementId: "G-265PX55B0Y"
};

// Inizializza Firebase
const app = initializeApp(firebaseConfig);

// Esporta autenticazione, database e storage
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const firebaseStorage = getStorage(app); // Mantieni anche questa se usata altrove

// Esporta onAuthStateChanged da Firebase
export { onAuthStateChanged } from "firebase/auth";

// COSTANTI E UTILITY
// Tipi di file consentiti per i certificati medici
const ALLOWED_FILE_TYPES = ['pdf', 'immagine/jpeg', 'immagine/png'];
// Dimensione massima del file (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Funzioni di utilità per la validazione dei file
const isValidFileType = (file) => {
  return ALLOWED_FILE_TYPES.includes(file.type);
};

const isValidFileSize = (file) => {
  return file.size <= MAX_FILE_SIZE;
};

// Genera un nome di file unico basato sul timestamp
const generateUniqueFileName = (originalFileName) => {
  const timestamp = new Date().getTime();
  const extension = originalFileName.split('.').pop().toLowerCase();
  return `${timestamp}_${Math.random().toString(36).substring(2, 8)}.${extension}`;
};

// Funzioni per l'autenticazione
export const loginWithEmail = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const registerWithEmail = async (email, password, userData) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  
  // Salva i dati utente nel database
  await setDoc(doc(db, "users", user.uid), {
    ...userData,
    email: user.email,
    createdAt: serverTimestamp(),
    role: userData.role || "user" // Default a "user" se non specificato
  });
  
  return userCredential;
};

export const logoutUser = () => {
  return signOut(auth);
};

// Funzioni per gli utenti
export const getUserData = async (userId) => {
  const docRef = doc(db, "users", userId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  
  return null;
};

export const getAllUsers = async () => {
  const usersCollection = collection(db, "users");
  const userSnapshot = await getDocs(usersCollection);
  
  return userSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const updateUserData = async (userId, userData) => {
  try {
    if (!userId) throw new Error("userId è obbligatorio");
    
    const userRef = doc(db, "users", userId);
    
    // Validazione del ruolo se presente
    if (userData.role) {
      const validRoles = ['user', 'responsabile', 'admin', 'hr'];
      if (!validRoles.includes(userData.role)) {
        throw new Error(`Ruolo non valido: ${userData.role}. Ruoli consentiti: ${validRoles.join(', ')}`);
      }
    }
    
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
    
    console.log(`updateUserData: Dati aggiornati per utente ${userId}`);
  } catch (error) {
    console.error("updateUserData: Errore nell'aggiornamento dei dati utente:", error);
    throw error;
  }
};

/**
 * Crea una richiesta permesso/ferie/malattia per un dipendente (funzione per responsabili)
 * @param {Object} requestData - Dati della richiesta
 * @returns {Promise<Object>} - I dati della richiesta creata
 */
export const createLeaveRequestForUser = async (requestData) => {
  try {
    console.log("createLeaveRequestForUser: Tentativo di creare una richiesta per dipendente:", requestData);
    
    // Validazione dei dati
    if (!requestData.type) throw new Error("Tipo di richiesta obbligatorio");
    if (!requestData.userId) throw new Error("userId del dipendente è obbligatorio");
    // La richiesta può arrivare da un responsabile di cantiere oppure da un
    // capo officina (per sé o per i suoi meccanici): i due form inviano campi diversi.
    const creatorId =
      requestData.createdByResponsabile?.responsabileId ||
      requestData.createdByCapoOfficina?.capoOfficinaId;
    if (!creatorId) throw new Error("ID responsabile obbligatorio");

    // Validazione specifica per tipo di richiesta
    if ((requestData.type === 'permission' || requestData.type === 'vacation') && !requestData.dateFrom) {
      throw new Error("Data obbligatoria per permessi e ferie");
    }
    
    if (requestData.type === 'vacation' && !requestData.dateTo) {
      throw new Error("Data fine obbligatoria per ferie");
    }
    
    // Validazione per malattia
    if (requestData.type === 'sickness') {
      if (!requestData.dateFrom) throw new Error("Data inizio obbligatoria per malattia");
      if (!requestData.protocolCode) throw new Error("Codice protocollo obbligatorio per malattia");
    }

    // Ottieni i dati dell'utente (dipendente) per cui si sta creando la richiesta
    const user = await getUserData(requestData.userId);
    if (!user) {
      throw new Error("Dipendente non trovato");
    }

    // Ottieni i dati del responsabile che sta creando la richiesta
    const responsabile = await getUserData(creatorId);
    if (!responsabile) {
      throw new Error("Responsabile non trovato");
    }

    // Prepara l'oggetto richiesta
    const requestObject = {
      ...requestData,
      userEmail: user.email || "",
      userName: user.nome && user.cognome ? 
        `${user.nome} ${user.cognome}` : user.email,
      status: "pending",
      createdAt: serverTimestamp(),
      lastUpdate: serverTimestamp(),
      
      // Informazioni di chi ha creato la richiesta. Anche per il capo officina
      // usiamo questo campo, così le viste esistenti mostrano il creatore.
      createdByResponsabile: {
        responsabileId: creatorId,
        responsabileName: responsabile.nome && responsabile.cognome ? 
          `${responsabile.nome} ${responsabile.cognome}` : responsabile.email,
        responsabileEmail: responsabile.email,
        createdAt: serverTimestamp()
      }
    };
    
    console.log("createLeaveRequestForUser: Oggetto richiesta preparato:", requestObject);
    
    // Salva nel database
    const leaveRequestsRef = collection(db, "leaveRequests");
    const docRef = await addDoc(leaveRequestsRef, requestObject);
    
    console.log(`createLeaveRequestForUser: Richiesta creata dal responsabile ${responsabile.email} per ${user.email} con ID: ${docRef.id}`);
    
    // Crea notifica per l'admin
    try {
      await NotificationService.notifyNewLeaveRequest({
        id: docRef.id,
        ...requestObject
      });
      console.log("createLeaveRequestForUser: Notifica admin creata con successo");
    } catch (notificationError) {
      console.error("createLeaveRequestForUser: Errore nella creazione della notifica:", notificationError);
    }
    
    // Crea notifica per il dipendente per informarlo della richiesta creata
    try {
      await NotificationService.notifyUserRequestCreatedByResponsabile({
        id: docRef.id,
        ...requestObject
      });
      console.log("createLeaveRequestForUser: Notifica dipendente creata con successo");
    } catch (notificationError) {
      console.error("createLeaveRequestForUser: Errore nella notifica al dipendente:", notificationError);
    }
    
    return {
      id: docRef.id,
      ...requestObject,
      createdAt: new Date(), // Converti per compatibilità con il componente
      lastUpdate: new Date()
    };
    
  } catch (error) {
    console.error("createLeaveRequestForUser: Errore nella creazione della richiesta:", error);
    throw new Error(`Errore nella creazione della richiesta: ${error.message}`);
  }
};

/**
 * Crea una notifica per tutti gli amministratori
 * @param {Object} notificationData - Dati della notifica
 * @param {string} notificationData.type - Tipo di notifica ('new_report', 'new_request')
 * @param {string} notificationData.title - Titolo della notifica
 * @param {string} notificationData.message - Messaggio della notifica
 * @param {string} notificationData.relatedId - ID dell'elemento correlato
 * @param {Object} notificationData.data - Dati aggiuntivi
 * @returns {Promise<Object>} - Notifica creata
 */
export const createAdminNotification = async (notificationData) => {
  try {
    console.log("createAdminNotification: Creazione notifica per admin", notificationData);
    
    // Ottieni la lista di tutti gli admin
    const adminUsers = await getAdminUsers();
    
    if (adminUsers.length === 0) {
      console.log("createAdminNotification: Nessun admin trovato");
      return null;
    }
    
    const adminIds = adminUsers.map(admin => admin.id);
    
    // Prepara i dati della notifica per admin
    const adminNotificationData = {
      ...notificationData,
      targetType: 'admin',
      targetAdmins: adminIds,
      readBy: [],
      createdAt: serverTimestamp()
    };
    
    // Salva la notifica nel database
    const notificationsRef = collection(db, "admin_notifications");
    const docRef = await addDoc(notificationsRef, adminNotificationData);
    
    console.log(`createAdminNotification: Notifica admin creata con ID: ${docRef.id}`);
    
    return {
      id: docRef.id,
      ...adminNotificationData,
      createdAt: new Date()
    };
  } catch (error) {
    console.error("createAdminNotification: Errore nella creazione della notifica admin:", error);
    throw error;
  }
};

/**
 * Recupera le notifiche non lette per un amministratore
 */
export const getAdminUnreadNotifications = async (adminId) => {
  try {
    console.log(`getAdminUnreadNotifications: Caricamento notifiche per adminId=${adminId}`);
    
    if (!adminId) throw new Error("adminId è obbligatorio");
    
    const notificationsRef = collection(db, "admin_notifications");
    const q = query(
      notificationsRef,
      where("targetAdmins", "array-contains", adminId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    console.log(`getAdminUnreadNotifications: Trovate ${querySnapshot.size} notifiche totali`);
    
    // Filtra solo quelle non lette da questo admin
    const notifications = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date()
        };
      })
      .filter(notification => {
        const readBy = notification.readBy || [];
        return !readBy.includes(adminId);
      });
    
    console.log(`getAdminUnreadNotifications: ${notifications.length} notifiche non lette`);
    return notifications;
  } catch (error) {
    console.error("getAdminUnreadNotifications: Errore nel recupero delle notifiche admin:", error);
    throw error;
  }
};

/**
 * Segna una notifica admin come letta da un amministratore
 */
export const markAdminNotificationAsRead = async (notificationId, adminId) => {
  try {
    console.log(`markAdminNotificationAsRead: Marcando notifica ${notificationId} come letta da admin ${adminId}`);
    
    const notificationRef = doc(db, "admin_notifications", notificationId);
    
    // Ottieni i dati attuali della notifica
    const notificationDoc = await getDoc(notificationRef);
    if (!notificationDoc.exists()) {
      console.log("markAdminNotificationAsRead: Notifica non trovata");
      return;
    }
    
    const notificationData = notificationDoc.data();
    const { targetAdmins, readBy = [] } = notificationData;
    
    // Se l'admin ha già letto, non fare nulla
    if (readBy.includes(adminId)) {
      console.log("markAdminNotificationAsRead: Admin ha già letto questa notifica");
      return;
    }
    
    // Aggiungi l'admin alla lista di quelli che hanno letto
    const newReadBy = [...readBy, adminId];
    await updateDoc(notificationRef, {
      readBy: newReadBy
    });
    
    // Se tutti gli admin hanno letto, elimina la notifica
    if (newReadBy.length >= targetAdmins.length) {
      console.log(`markAdminNotificationAsRead: Tutti gli admin hanno letto la notifica ${notificationId}, eliminazione...`);
      await deleteDoc(notificationRef);
    }
    
    console.log(`markAdminNotificationAsRead: Notifica ${notificationId} marcata come letta`);
    
  } catch (error) {
    console.error("markAdminNotificationAsRead: Errore nel marcare la notifica come letta:", error);
    throw error;
  }
};

/**
 * Recupera tutti gli utenti amministratori
 */
export const getAdminUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    
    // Query per admin
    const adminQuery = query(usersRef, where("role", "==", "admin"));
    const adminSnapshot = await getDocs(adminQuery);
    
    // Query per HR
    const hrQuery = query(usersRef, where("role", "==", "hr"));
    const hrSnapshot = await getDocs(hrQuery);
    
    // Combina i risultati
    const users = [];
    
    adminSnapshot.docs.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    hrSnapshot.docs.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`getAdminUsers: Trovati ${users.length} admin/HR`);
    
    return users;
  } catch (error) {
    console.error("getAdminUsers: Errore:", error);
    throw error;
  }
};

/**
 * Notifica gli admin per una nuova segnalazione
 */
export const notifyAdminsNewReport = async (report) => {
  try {
    const userName = report.userName || report.userEmail || "Utente";
    const reportDate = report.createdAt ? 
      (report.createdAt.seconds ? 
        new Date(report.createdAt.seconds * 1000).toLocaleDateString('it-IT') :
        report.createdAt.toLocaleDateString('it-IT')
      ) : 
      new Date().toLocaleDateString('it-IT');
    
    await createAdminNotification({
      type: 'new_report',
      title: 'Nuova segnalazione ricevuta',
      message: `${userName} ha inviato una nuova segnalazione il ${reportDate}`,
      relatedId: report.id,
      data: {
        reportId: report.id,
        userName: userName,
        category: report.category || 'Non specificata',
        priority: report.priority || 'normale',
        description: report.description ? report.description.substring(0, 100) + '...' : ''
      }
    });
    
    console.log("notifyAdminsNewReport: Notifica creata per nuova segnalazione");
  } catch (error) {
    console.error("notifyAdminsNewReport: Errore nella notifica per nuova segnalazione:", error);
  }
};

/**
 * Notifica gli admin per una nuova richiesta (permesso/ferie/malattia)
 */
export const notifyAdminsNewRequest = async (request) => {
  try {
    const userName = request.userName || request.userEmail || "Utente";
    const requestDate = request.createdAt ? 
      (request.createdAt.seconds ? 
        new Date(request.createdAt.seconds * 1000).toLocaleDateString('it-IT') :
        request.createdAt.toLocaleDateString('it-IT')
      ) : 
      new Date().toLocaleDateString('it-IT');
    
    const requestTypeNames = {
      'permission': 'permesso',
      'vacation': 'ferie',
      'sickness': 'malattia'
    };
    
    const requestTypeName = requestTypeNames[request.type] || request.type;
    
    // Costruisci informazioni sulle date
    let dateInfo = '';
    if (request.dateFrom) {
      dateInfo = `dal ${request.dateFrom}`;
      if (request.dateTo && request.dateTo !== request.dateFrom) {
        dateInfo += ` al ${request.dateTo}`;
      }
    }
    
    const message = `${userName} ha inviato una richiesta di ${requestTypeName} ${dateInfo ? dateInfo : 'il ' + requestDate}`;
    
    await createAdminNotification({
      type: 'new_request',
      title: `Nuova richiesta di ${requestTypeName}`,
      message: message,
      relatedId: request.id,
      data: {
        requestId: request.id,
        userName: userName,
        requestType: request.type,
        dateFrom: request.dateFrom || '',
        dateTo: request.dateTo || '',
        permissionType: request.permissionType || ''
      }
    });
    
    console.log("notifyAdminsNewRequest: Notifica creata per nuova richiesta");
  } catch (error) {
    console.error("notifyAdminsNewRequest: Errore nella notifica per nuova richiesta:", error);
  }
};

// Funzioni per le segnalazioni (reports)
export const addReport = async (reportData) => {
  try {
    const reportsCollection = collection(db, "reports");
    const docRef = await addDoc(reportsCollection, {
      ...reportData,
      createdAt: serverTimestamp(),
      lastUpdate: serverTimestamp()
    });
    return { id: docRef.id, ...reportData };
  } catch (error) {
    console.error("Errore nell'aggiunta della segnalazione:", error);
    throw error;
  }
};

export const getAllReports = async () => {
  const reportsCollection = collection(db, "reports");
  const q = query(reportsCollection, orderBy("createdAt", "desc"));
  const reportSnapshot = await getDocs(q);
  
  return reportSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    lastUpdate: doc.data().lastUpdate?.toDate?.() || new Date()
  }));
};

export const getUserReportsByMonth = async (userId, month, year) => {
  console.log(`getUserReportsByMonth: Avvio con userId=${userId}, month=${month}, year=${year}`);
  
  try {
    if (!userId) throw new Error("userId è obbligatorio");
    if (!month) throw new Error("month è obbligatorio");
    if (!year) throw new Error("year è obbligatorio");
    
    // Rimuovi eventuali prefissi (come "prev-") e zeri iniziali
    let normalizedMonth = month.toString().replace(/^prev-/, '').replace(/^0+/, '');
    
    // Se il mese è vuoto dopo la normalizzazione, usa il valore originale
    if (!normalizedMonth) normalizedMonth = month;
    
    // Formato con zero iniziale per query alternative
    let paddedMonth = normalizedMonth.length === 1 ? normalizedMonth.padStart(2, '0') : normalizedMonth;
    
    console.log(`getUserReportsByMonth: Parametri normalizzati - userId=${userId}, normalizedMonth=${normalizedMonth}, paddedMonth=${paddedMonth}, year=${year}`);
    
    const reportsCollection = collection(db, "reports");
    
    // Prima prova con il formato normalizzato
    let q = query(
      reportsCollection, 
      where("userId", "==", userId),
      where("month", "==", normalizedMonth),
      where("year", "==", year)
    );
    
    console.log("getUserReportsByMonth: Esecuzione query con mese normalizzato...");
    let querySnapshot = await getDocs(q);
    
    // Se non trova risultati, prova con il formato con zero iniziale
    if (querySnapshot.empty && normalizedMonth !== paddedMonth) {
      console.log(`getUserReportsByMonth: Nessun risultato trovato, tento con month=${paddedMonth}`);
      
      q = query(
        reportsCollection, 
        where("userId", "==", userId),
        where("month", "==", paddedMonth),
        where("year", "==", year)
      );
      
      querySnapshot = await getDocs(q);
    }
    
    // Se ancora non trova risultati, prova una query più generica
    if (querySnapshot.empty) {
      console.log("getUserReportsByMonth: Nessun risultato trovato con entrambi i formati, eseguo query generica");
      
      q = query(
        reportsCollection, 
        where("userId", "==", userId),
        where("year", "==", year)
      );
      
      querySnapshot = await getDocs(q);
      
      // Filtra manualmente i risultati per il mese
      if (!querySnapshot.empty) {
        const allResults = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            lastUpdate: data.lastUpdate?.toDate?.() || new Date()
          };
        });
        
        // Filtra i report che corrispondono al mese (sia formato con zero che senza zero)
        const filteredResults = allResults.filter(report => {
          const reportMonth = report.month.toString().replace(/^0+/, '');
          return reportMonth === normalizedMonth;
        });
        
        console.log(`getUserReportsByMonth: Query generica trovata, filtrati ${filteredResults.length} risultati per il mese ${normalizedMonth}`);
        return filteredResults;
      }
    }
    
    console.log(`getUserReportsByMonth: Query completata, ${querySnapshot.size} risultati trovati`);
    
    // Converti i dati dal formato Firestore
    const reports = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastUpdate: data.lastUpdate?.toDate?.() || new Date()
      };
    });
    
    console.log("getUserReportsByMonth: Segnalazioni caricate:", reports);
    return reports;
  } catch (error) {
    console.error("getUserReportsByMonth: Errore nel recupero delle segnalazioni:", error);
    throw error;
  }
};

export const submitReport = async (reportData, userId) => {
  console.log("submitReport: Tentativo di inviare una segnalazione:", reportData);
  console.log("submitReport: userId:", userId);
  
  if (!reportData) throw new Error("reportData è obbligatorio");
  if (!userId) throw new Error("userId è obbligatorio");
  if (!reportData.date) throw new Error("date è obbligatorio");
  if (!reportData.description) throw new Error("description è obbligatorio");
  
  try {
    // Ottieni i dati dell'utente
    const user = await getUserData(userId);
    console.log("submitReport: Dati utente ottenuti:", user);
    
    if (!user) {
      throw new Error("Utente non trovato");
    }
    
    // Estrai mese e anno dalla data della segnalazione
    let year, month;
    
    if (reportData.date.includes('-')) {
      // Se è nel formato YYYY-MM-DD
      const dateParts = reportData.date.split("-");
      year = dateParts[0];
      month = dateParts[1];
      
      // Rimuovi eventuali zeri iniziali dal mese
      month = month.replace(/^0+/, '');
    } else if (reportData.date.includes('/')) {
      // Se è nel formato DD/MM/YYYY
      const dateParts = reportData.date.split("/");
      year = dateParts[2];
      month = dateParts[1];
      
      // Rimuovi eventuali zeri iniziali dal mese
      month = month.replace(/^0+/, '');
    } else {
      // Fallback: usa l'anno e il mese corrente
      const now = new Date();
      year = now.getFullYear().toString();
      month = (now.getMonth() + 1).toString();
    }
    
    console.log(`submitReport: Data parsata - anno: ${year}, mese: ${month}`);
    
    // Crea l'oggetto segnalazione
    const reportObject = {
      ...reportData,
      userId,
      userEmail: user.email || "",
      userName: user.nome && user.cognome ? 
        `${user.nome} ${user.cognome}` : user.email,
      month,
      year,
      status: "In attesa",
      createdAt: serverTimestamp(),
      lastUpdate: serverTimestamp()
    };
    
    console.log("submitReport: Oggetto segnalazione completo:", reportObject);
    
    // Aggiungi la segnalazione alla collezione reports
    const reportsCollection = collection(db, "reports");
    const docRef = await addDoc(reportsCollection, reportObject);
    
    console.log("submitReport: Segnalazione inviata con successo, ID:", docRef.id);
    
    // NOVITÀ: Crea la segnalazione finale con ID per le notifiche
    const finalReport = {
      id: docRef.id,
      ...reportObject,
      createdAt: new Date(),
      lastUpdate: new Date()
    };
    
    // NOVITÀ: Notifica tutti gli admin della nuova segnalazione
    try {
      await notifyAdminsNewReport(finalReport);
      console.log("submitReport: Notifiche admin inviate con successo");
    } catch (notificationError) {
      console.error('submitReport: Errore nella notifica admin per nuova segnalazione:', notificationError);
      // Non bloccare la creazione della segnalazione per errori di notifica
    }
    
    return finalReport;
  } catch (error) {
    console.error("submitReport: Errore nell'invio della segnalazione:", error);
    throw error;
  }
};

export const updateReportStatus = async (reportId, newStatus, adminNotes = '') => {
  console.log('===== INIZIO updateReportStatus =====');
  console.log(`Parametri ricevuti: reportId=${reportId}, newStatus=${newStatus}, adminNotes="${adminNotes}"`);
  console.log('VERSIONE CON FIX 2024-12-06');
  
  try {
    if (!reportId) throw new Error("reportId è obbligatorio");
    if (!newStatus) throw new Error("newStatus è obbligatorio");
    
    const reportRef = doc(db, "reports", reportId);
    
    // STEP 1: Recupera i dati ATTUALI dal database
    console.log('STEP 1: Recupero dati attuali dal database...');
    const docSnapBefore = await getDoc(reportRef);
    
    if (!docSnapBefore.exists()) {
      throw new Error(`Segnalazione con ID ${reportId} non trovata`);
    }
    
    const dataBeforeUpdate = docSnapBefore.data();
    const oldStatus = dataBeforeUpdate.status;
    
    console.log('STEP 1 COMPLETATO - Dati PRIMA dell\'aggiornamento:', {
      reportId: reportId,
      oldStatus: oldStatus,
      newStatus: newStatus,
      tuttiIDati: dataBeforeUpdate
    });
    
    console.log(`>>> CAMBIO STATO RILEVATO: "${oldStatus}" → "${newStatus}"`);
    
    // STEP 2: Prepara i dati da aggiornare
    console.log('STEP 2: Preparazione dati per aggiornamento...');
    const updateData = {
      status: newStatus,
      lastUpdate: serverTimestamp()
    };
    
    if (adminNotes && adminNotes.trim()) {
      updateData.adminNotes = adminNotes.trim();
      updateData.adminNotesDate = serverTimestamp();
    }
    
    console.log('Dati che verranno aggiornati:', updateData);
    
    // STEP 3: Aggiorna il documento
    console.log('STEP 3: Aggiornamento documento nel database...');
    await updateDoc(reportRef, updateData);
    console.log('STEP 3 COMPLETATO - Documento aggiornato con successo');
    
    // STEP 4: Verifica (opzionale) - Rileggi per conferma
    console.log('STEP 4: Verifica post-aggiornamento...');
    const docSnapAfter = await getDoc(reportRef);
    const dataAfterUpdate = docSnapAfter.data();
    console.log('Stato DOPO aggiornamento:', dataAfterUpdate.status);
    
    // STEP 5: Creazione notifica
    console.log('STEP 5: Gestione notifiche...');
    try {
      console.log('Controllo se creare notifica:', {
        oldStatus: oldStatus,
        newStatus: newStatus,
        sonoUguali: oldStatus === newStatus,
        tipoOldStatus: typeof oldStatus,
        tipoNewStatus: typeof newStatus
      });
      
      // Verifica che ci sia un cambio di stato significativo
      if (oldStatus !== newStatus && ['Presa in carico', 'Conclusa'].includes(newStatus)) {
        console.log('✓ Cambio di stato significativo rilevato, creazione notifica...');
        
        const notification = await NotificationService.notifyReportStatusChange(
          dataBeforeUpdate.userId,
          reportId,
          dataBeforeUpdate,
          oldStatus,
          newStatus,
          adminNotes
        );
        
        console.log('✓ Notifica creata con successo:', notification);
      } else {
        console.log('✗ Cambio di stato non significativo, notifica NON creata');
        if (oldStatus === newStatus) {
          console.log('   Motivo: oldStatus e newStatus sono uguali');
        } else {
          console.log('   Motivo: newStatus non è tra quelli significativi');
        }
      }
      
    } catch (notificationError) {
      console.error('✗ Errore nella creazione della notifica:', notificationError);
      console.error('Stack trace:', notificationError.stack);
    }
    
    console.log('===== FINE updateReportStatus =====');
    return true;
    
  } catch (error) {
    console.error('===== ERRORE in updateReportStatus =====');
    console.error('Errore:', error);
    console.error('Stack:', error.stack);
    throw error;
  }
};

export const saveWorkHours = async (userId, month, year, entries) => {
  try {
    console.log(`saveWorkHours: userId=${userId}, month=${month}, year=${year}`);
    console.log(`Entries da salvare:`, entries.length);
    
    // Normalizza il mese
    const normalizedMonth = month.toString().replace(/^0+/, '');
    
    // ID del documento
    const docId = `${userId}_${normalizedMonth}_${year}`;
    const docRef = doc(db, "workHours", docId);
    
    // VERSIONE CORRETTA: Prepara le entries per il salvataggio con gestione migliorata delle lettere speciali
    const processedEntries = entries.map((entry, index) => {
      let totalValue = entry.total;
      
      // Debug specifico per CIG
      if (String(entry.total).includes('CIG')) {
        console.log(`🔍 DEBUG CIG Entry ${index}:`, {
          date: entry.date,
          originalTotal: entry.total,
          totalType: typeof entry.total,
          totalValue: JSON.stringify(entry.total),
          trimmed: String(entry.total).trim(),
          upperCase: String(entry.total).trim().toUpperCase()
        });
      }
      
      // Normalizza il valore per un controllo più robusto
      const normalizedTotal = String(totalValue).trim().toUpperCase();
      
      // VERSIONE ROBUSTA: Lista delle lettere speciali con controllo normalizzato
      const specialLetters = ["M", "P", "F", "A", "C"];
      
      if (specialLetters.includes(normalizedTotal)) {
        totalValue = normalizedTotal; // Usa il valore normalizzato
        console.log(`✅ Mantenendo valore speciale normalizzato: "${totalValue}" per data ${entry.date}`);
      } else {
        // Altrimenti converti in numero
        const numericValue = parseInt(entry.total) || 0;
        totalValue = numericValue;
        console.log(`🔢 Convertendo in numero: ${entry.total} → ${totalValue} per data ${entry.date}`);
      }
      
      const processedEntry = {
        date: entry.date,
        day: entry.day,
        total: totalValue,
        overtime: parseInt(entry.overtime) || 0,
        notes: entry.notes || "",
        isWeekend: entry.isWeekend || false
      };
      
      // Log finale per CIG
      if (String(entry.total).includes('CIG')) {
        console.log(`🎯 CIG Entry processata:`, processedEntry);
      }
      
      return processedEntry;
    });
    
    // Log riassuntivo delle lettere speciali salvate
    const specialEntries = processedEntries.filter(entry => 
      ["M", "P", "F", "A", "C"].includes(String(entry.total).toUpperCase())
    );
    console.log(`📊 Riassunto lettere speciali da salvare (${specialEntries.length}):`, 
      specialEntries.map(e => `${e.date}: ${e.total}`));
    
    // Salva il documento
    await setDoc(docRef, {
      userId,
      month: normalizedMonth,
      year: year.toString(),
      entries: processedEntries,
      lastUpdated: serverTimestamp()
    });
    
    console.log(`✅ WorkHours salvate con successo: ${docId}`);
    
    // Verifica finale (opzionale per debug)
    const savedDoc = await getDoc(docRef);
    if (savedDoc.exists()) {
      const savedData = savedDoc.data();
      const savedSpecialEntries = savedData.entries.filter(entry => 
        ["M", "P", "F", "A", "C"].includes(String(entry.total).toUpperCase())
      );
      console.log(`🔍 Verifica post-salvataggio - lettere speciali salvate (${savedSpecialEntries.length}):`, 
        savedSpecialEntries.map(e => `${e.date}: ${e.total}`));
    }
    
    return { success: true, docId };
    
  } catch (error) {
    console.error("❌ Errore nel salvataggio delle ore lavorative:", error);
    throw error;
  }
};

export const getWorkHours = async (userId, month, year) => {
  try {
    // Normalizza il mese
    let normalizedMonth = month.toString().replace(/^prev-/, '').replace(/^0+/, '');
    if (!normalizedMonth) normalizedMonth = month;
    
    // Crea un documento ID combinando utente, mese e anno
    const docId = `${userId}_${normalizedMonth}_${year}`;
    console.log(`getWorkHours: Tentativo di recuperare documento con ID=${docId}`);
    
    const workHoursRef = doc(db, "workHours", docId);
    const docSnap = await getDoc(workHoursRef);
    
    if (docSnap.exists()) {
      console.log(`getWorkHours: Documento trovato con ID=${docId}`);
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    
    // Se non trova documento con il primo formato, prova con il mese con zero iniziale
    const paddedMonth = normalizedMonth.length === 1 ? normalizedMonth.padStart(2, '0') : normalizedMonth;
    if (paddedMonth !== normalizedMonth) {
      const altDocId = `${userId}_${paddedMonth}_${year}`;
      console.log(`getWorkHours: Tentativo alternativo con ID=${altDocId}`);
      
      const altWorkHoursRef = doc(db, "workHours", altDocId);
      const altDocSnap = await getDoc(altWorkHoursRef);
      
      if (altDocSnap.exists()) {
        console.log(`getWorkHours: Documento alternativo trovato con ID=${altDocId}`);
        return {
          id: altDocSnap.id,
          ...altDocSnap.data()
        };
      }
    }
    
    console.log("getWorkHours: Nessun documento trovato");
    return null;
  } catch (error) {
    console.error("getWorkHours: Errore nel recupero delle ore lavorative:", error);
    throw error;
  }
};

// 1. SOSTITUIRE la funzione getUserWorkHoursByMonth esistente con questa versione:

export const getUserWorkHoursByMonth = async (userId, month, year) => {
  try {
    console.log(`getUserWorkHoursByMonth: userId=${userId}, month=${month}, year=${year}`);
    
    // Normalizza il mese
    const normalizedMonth = month.toString().replace(/^prev-/, '').replace(/^0+/, '');
    const monthInt = parseInt(normalizedMonth);
    const yearInt = parseInt(year);
    
    // NUOVA FUNZIONE: Genera il calendario completo del mese
    const generateCompleteMonth = (month, year) => {
      const daysInMonth = new Date(year, month, 0).getDate();
      const entries = [];
      const dayNames = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        entries.push({
          date: dateStr,
          day: dayNames[dayOfWeek],
          total: 0,
          overtime: 0,
          notes: "",
          isWeekend: isWeekend,
          hasData: false
        });
      }
      
      return entries.sort((a, b) => new Date(a.date) - new Date(b.date));
    };
    
    // Genera il calendario completo
    const completeMonth = generateCompleteMonth(monthInt, yearInt);
    
    // Cerca i dati esistenti (mantieni la logica esistente di ricerca)
    let existingData = null;
    
    // Tentativo 1: ID documento senza zero iniziale
    const docId = `${userId}_${normalizedMonth}_${year}`;
    console.log(`Tentativo 1: docId=${docId}`);
    
    const docRef = doc(db, "workHours", docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      console.log(`Documento trovato: ${docId}`);
      const data = docSnap.data();
      if (data.entries && Array.isArray(data.entries)) {
        existingData = data.entries;
      }
    } else {
      // Tentativo 2: ID documento con zero iniziale
      const formattedMonth = normalizedMonth.length === 1 ? normalizedMonth.padStart(2, '0') : normalizedMonth;
      const altDocId = `${userId}_${formattedMonth}_${year}`;
      
      console.log(`Tentativo 2: altDocId=${altDocId}`);
      
      if (altDocId !== docId) {
        const altDocRef = doc(db, "workHours", altDocId);
        const altDocSnap = await getDoc(altDocRef);
        
        if (altDocSnap.exists()) {
          console.log(`Documento alternativo trovato: ${altDocId}`);
          const altData = altDocSnap.data();
          if (altData.entries && Array.isArray(altData.entries)) {
            existingData = altData.entries;
          }
        }
      }
      
      // Tentativo 3: Query generale
      if (!existingData) {
        console.log(`Tentativo 3: Query generale`);
        const workHoursRef = collection(db, "workHours");
        const q = query(
          workHoursRef, 
          where("userId", "==", userId),
          where("year", "==", year)
        );
        
        const querySnapshot = await getDocs(q);
        console.log(`Query generale: ${querySnapshot.size} documenti trovati`);
        
        if (!querySnapshot.empty) {
          for (const doc of querySnapshot.docs) {
            const data = doc.data();
            const docMonth = data.month.toString().replace(/^0+/, '');
            
            if (docMonth === normalizedMonth) {
              console.log(`Corrispondenza trovata per mese ${docMonth}`);
              if (data.entries && Array.isArray(data.entries)) {
                existingData = data.entries;
                break;
              }
            }
          }
        }
      }
    }
    
    // NUOVA LOGICA: Merge dei dati esistenti con il calendario completo
    if (existingData && existingData.length > 0) {
      console.log(`Merge di ${existingData.length} entries esistenti con calendario completo`);
      
      // Crea una mappa dei dati esistenti
      const existingDataMap = {};
      existingData.forEach(entry => {
        if (entry.date) {
          existingDataMap[entry.date] = {
            ...entry,
            overtime: entry.overtime !== undefined ? entry.overtime : 0,
            hasData: true
          };
        }
      });
      
      // Merge con il calendario completo
      const mergedEntries = completeMonth.map(dayEntry => {
        const existingEntry = existingDataMap[dayEntry.date];
        if (existingEntry) {
          return existingEntry;
        }
        return dayEntry;
      });
      
      return {
        userId,
        month: normalizedMonth,
        year,
        entries: mergedEntries,
        lastUpdated: new Date().toISOString()
      };
    } else {
      console.log(`Nessun dato esistente, restituisco calendario vuoto`);
      
      return {
        userId,
        month: normalizedMonth,
        year,
        entries: completeMonth,
        lastUpdated: new Date().toISOString()
      };
    }
    
  } catch (error) {
    console.error("Errore nel recupero delle ore lavorative:", error);
    throw error;
  }
};

// =============================================================================
// FUNZIONI PER LA GESTIONE DELLE NOTIFICHE
// =============================================================================

/**
 * Crea una nuova notifica per un utente
 * @param {string} userId - ID dell'utente destinatario
 * @param {string} type - Tipo di notifica ('report_status' | 'request_status')
 * @param {string} relatedId - ID della segnalazione/richiesta correlata
 * @param {string} title - Titolo della notifica
 * @param {string} message - Messaggio della notifica
 * @param {Object} data - Dati aggiuntivi specifici per tipo
 * @returns {Promise<Object>} - Dati della notifica creata
 */
export const createNotification = async (userId, type, relatedId, title, message, data = {}) => {
  try {
    console.log(`createNotification: Creazione notifica per userId=${userId}, type=${type}`);
    
    if (!userId) throw new Error("userId è obbligatorio");
    if (!type) throw new Error("type è obbligatorio");
    if (!relatedId) throw new Error("relatedId è obbligatorio");
    if (!title) throw new Error("title è obbligatorio");
    if (!message) throw new Error("message è obbligatorio");
    
    // Validazione del tipo
    const validTypes = ['report_status', 'request_status'];
    if (!validTypes.includes(type)) {
      throw new Error(`Tipo di notifica non valido: ${type}. Tipi supportati: ${validTypes.join(', ')}`);
    }
    
    // Prepara i dati della notifica
    const notificationData = {
      userId,
      type,
      relatedId,
      title,
      message,
      data,
      createdAt: serverTimestamp()
    };
    
    console.log("createNotification: Dati notifica:", notificationData);
    
    // AGGIUNGI QUESTO LOG PRIMA DI SALVARE
    console.log('createNotification: Tentativo di salvare nel database...');
    
    // Salva la notifica nel database
    const notificationsRef = collection(db, "notifications");
    const docRef = await addDoc(notificationsRef, notificationData);
    
    // AGGIUNGI QUESTO LOG DOPO IL SALVATAGGIO
    console.log('createNotification: Documento salvato con successo:', docRef.id);
    console.log(`createNotification: Notifica creata con successo, ID: ${docRef.id}`);
    
    return {
      id: docRef.id,
      ...notificationData,
      createdAt: new Date()
    };
  } catch (error) {
    console.error("createNotification: Errore nella creazione della notifica:", error);
    throw error;
  }
};

/**
 * Recupera tutte le notifiche non lette di un utente (versione corretta)
 * @param {string} userId - ID dell'utente
 * @returns {Promise<Array>} - Array delle notifiche non lette
 */
export const getUserUnreadNotifications = async (userId) => {
  try {
    console.log(`getUserUnreadNotifications: Caricamento notifiche per userId=${userId}`);
    
    if (!userId) throw new Error("userId è obbligatorio");
    
    const notificationsRef = collection(db, "notifications");
    
    // CORREZIONE: Query semplice senza orderBy per evitare errori di indice
    const q = query(
      notificationsRef,
      where("userId", "==", userId)
    );
    
    const querySnapshot = await getDocs(q);
    console.log(`getUserUnreadNotifications: Trovate ${querySnapshot.size} notifiche`);
    
    const notifications = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      // Gestisci diversi formati di timestamp
      let createdAt;
      if (data.createdAt?.toDate && typeof data.createdAt.toDate === 'function') {
        // Timestamp di Firestore
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // Date object
        createdAt = data.createdAt;
      } else if (typeof data.createdAt === 'string') {
        // String date
        createdAt = new Date(data.createdAt);
      } else {
        // Fallback a data corrente
        createdAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt
      };
    });
    
    // Ordina manualmente le notifiche per data di creazione (più recenti prima)
    notifications.sort((a, b) => b.createdAt - a.createdAt);
    
    console.log("getUserUnreadNotifications: Notifiche caricate e ordinate:", notifications.length);
    return notifications;
  } catch (error) {
    console.error("getUserUnreadNotifications: Errore nel caricamento delle notifiche:", error);
    throw error;
  }
};

/**
 * Segna una notifica come letta (la elimina)
 * @param {string} notificationId - ID della notifica
 * @returns {Promise<boolean>} - true se l'operazione è riuscita
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    console.log(`markNotificationAsRead: Eliminazione notifica ID=${notificationId}`);
    
    if (!notificationId) throw new Error("notificationId è obbligatorio");
    
    const notificationRef = doc(db, "notifications", notificationId);
    
    // Verifica che la notifica esista
    const notificationDoc = await getDoc(notificationRef);
    if (!notificationDoc.exists()) {
      throw new Error(`Notifica con ID ${notificationId} non trovata`);
    }
    
    // Elimina la notifica (segnarla come letta = eliminarla)
    await deleteDoc(notificationRef);
    
    console.log(`markNotificationAsRead: Notifica ${notificationId} eliminata con successo`);
    return true;
  } catch (error) {
    console.error("markNotificationAsRead: Errore nell'eliminazione della notifica:", error);
    throw error;
  }
};

/**
 * Segna tutte le notifiche di un utente come lette (le elimina tutte)
 * @param {string} userId - ID dell'utente
 * @returns {Promise<number>} - Numero di notifiche eliminate
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    console.log(`markAllNotificationsAsRead: Eliminazione tutte le notifiche per userId=${userId}`);
    
    if (!userId) throw new Error("userId è obbligatorio");
    
    // Recupera tutte le notifiche dell'utente
    const notificationsRef = collection(db, "notifications");
    const q = query(notificationsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    console.log(`markAllNotificationsAsRead: Trovate ${querySnapshot.size} notifiche da eliminare`);
    
    if (querySnapshot.empty) {
      console.log("markAllNotificationsAsRead: Nessuna notifica da eliminare");
      return 0;
    }
    
    // Elimina tutte le notifiche in batch
    const batch = [];
    let deletedCount = 0;
    
    // Firestore ha un limite di 500 operazioni per batch
    for (const doc of querySnapshot.docs) {
      batch.push(deleteDoc(doc.ref));
      deletedCount++;
      
      // Se raggiungiamo 500 operazioni, esegui il batch e ricomincia
      if (batch.length === 500) {
        await Promise.all(batch);
        batch.length = 0; // Svuota l'array
      }
    }
    
    // Esegui le operazioni rimanenti
    if (batch.length > 0) {
      await Promise.all(batch);
    }
    
    console.log(`markAllNotificationsAsRead: ${deletedCount} notifiche eliminate con successo`);
    return deletedCount;
  } catch (error) {
    console.error("markAllNotificationsAsRead: Errore nell'eliminazione delle notifiche:", error);
    throw error;
  }
};

/**
 * Elimina una singola notifica (funzione di utilità)
 * @param {string} notificationId - ID della notifica
 * @returns {Promise<boolean>} - true se l'operazione è riuscita
 */
export const deleteNotification = async (notificationId) => {
  try {
    console.log(`deleteNotification: Eliminazione notifica ID=${notificationId}`);
    
    if (!notificationId) throw new Error("notificationId è obbligatorio");
    
    const notificationRef = doc(db, "notifications", notificationId);
    
    // Verifica che la notifica esista
    const notificationDoc = await getDoc(notificationRef);
    if (!notificationDoc.exists()) {
      throw new Error(`Notifica con ID ${notificationId} non trovata`);
    }
    
    // Elimina la notifica
    await deleteDoc(notificationRef);
    
    console.log(`deleteNotification: Notifica ${notificationId} eliminata con successo`);
    return true;
  } catch (error) {
    console.error("deleteNotification: Errore nell'eliminazione della notifica:", error);
    throw error;
  }
};

/**
 * Conta il numero di notifiche non lette di un utente
 * @param {string} userId - ID dell'utente
 * @returns {Promise<number>} - Numero di notifiche non lette
 */
export const getUnreadNotificationsCount = async (userId) => {
  try {
    console.log(`getUnreadNotificationsCount: Conteggio notifiche per userId=${userId}`);
    
    if (!userId) throw new Error("userId è obbligatorio");
    
    const notificationsRef = collection(db, "notifications");
    const q = query(notificationsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const count = querySnapshot.size;
    console.log(`getUnreadNotificationsCount: Trovate ${count} notifiche non lette`);
    
    return count;
  } catch (error) {
    console.error("getUnreadNotificationsCount: Errore nel conteggio delle notifiche:", error);
    throw error;
  }
};

// =============================================================================
// FUNZIONI DI UTILITÀ PER CREARE NOTIFICHE SPECIFICHE
// =============================================================================

/**
 * Crea una notifica per il cambio stato di una segnalazione
 * @param {string} userId - ID dell'utente
 * @param {string} reportId - ID della segnalazione
 * @param {string} oldStatus - Stato precedente
 * @param {string} newStatus - Nuovo stato
 * @param {string} reportDate - Data della segnalazione
 * @param {string} adminNotes - Note dell'amministratore (opzionale)
 * @returns {Promise<Object>} - Notifica creata
 */
export const createReportStatusNotification = async (userId, reportId, oldStatus, newStatus, reportDate, adminNotes = '') => {
  try {
    // Mappatura degli stati in italiano
    const statusMapping = {
      'In attesa': 'in attesa',
      'Presa in carico': 'presa in carico',
      'Conclusa': 'conclusa'
    };
    
    const newStatusText = statusMapping[newStatus] || newStatus;
    
    // Prepara il titolo e il messaggio
    let title = '';
    let message = '';
    
    switch (newStatus) {
      case 'Presa in carico':
        title = 'Segnalazione presa in carico';
        message = `La tua segnalazione del ${formatDateForNotification(reportDate)} è stata presa in carico e verrà verificata a breve.`;
        break;
      case 'Conclusa':
        title = 'Segnalazione conclusa';
        message = `La tua segnalazione del ${formatDateForNotification(reportDate)} è stata conclusa.`;
        break;
      default:
        title = 'Segnalazione aggiornata';
        message = `La tua segnalazione del ${formatDateForNotification(reportDate)} è stata aggiornata.`;
    }
    
    // Aggiungi le note dell'admin al messaggio se presenti
    if (adminNotes && adminNotes.trim()) {
      message += ` Note: ${adminNotes}`;
    }
    
    // Dati aggiuntivi
    const data = {
      reportDate,
      oldStatus,
      newStatus,
      adminNotes: adminNotes || ''
    };
    
    return await createNotification(userId, 'report_status', reportId, title, message, data);
  } catch (error) {
    console.error("createReportStatusNotification: Errore:", error);
    throw error;
  }
};

/**
 * Crea una notifica per il cambio stato di una richiesta
 * @param {string} userId - ID dell'utente
 * @param {string} requestId - ID della richiesta
 * @param {Object} requestData - Dati della richiesta
 * @param {string} oldStatus - Stato precedente
 * @param {string} newStatus - Nuovo stato
 * @param {string} adminNotes - Note dell'amministratore (opzionale)
 * @returns {Promise<Object>} - Notifica creata
 */
export const createRequestStatusNotification = async (userId, requestId, requestData, oldStatus, newStatus, adminNotes = '') => {
  try {
    // Mappatura dei tipi di richiesta
    const typeMapping = {
      'permission': 'permesso',
      'vacation': 'ferie',
      'sickness': 'malattia'
    };
    
    const requestTypeText = typeMapping[requestData.type] || requestData.type;
    
    // Mappatura degli stati
    const statusMapping = {
      'pending': 'in attesa',
      'approved': 'approvata',
      'rejected': 'rifiutata'
    };
    
    const newStatusText = statusMapping[newStatus] || newStatus;
    
    // Prepara il titolo e il messaggio
    let title = '';
    let message = '';
    
    // Costruisci le informazioni sulle date
    let dateInfo = '';
    if (requestData.dateFrom) {
      dateInfo = formatDateForNotification(requestData.dateFrom);
      if (requestData.dateTo && requestData.dateTo !== requestData.dateFrom) {
        dateInfo += ` - ${formatDateForNotification(requestData.dateTo)}`;
      }
    }
    
    // Personalizza il messaggio in base al tipo e stato
    switch (newStatus) {
      case 'approved':
        title = `Richiesta ${requestTypeText} approvata`;
        message = `La tua richiesta di ${requestTypeText}`;
        if (dateInfo) message += ` del ${dateInfo}`;
        message += ' è stata approvata.';
        
        // Aggiungi info specifica per ferie/permessi
        if (requestData.type === 'vacation' || requestData.type === 'permission') {
          message += ' Le date sono state automaticamente aggiunte al tuo calendario ore.';
        }
        break;
        
      case 'rejected':
        title = `Richiesta ${requestTypeText} rifiutata`;
        message = `La tua richiesta di ${requestTypeText}`;
        if (dateInfo) message += ` del ${dateInfo}`;
        message += ' è stata rifiutata.';
        break;
        
      default:
        title = `Richiesta ${requestTypeText} aggiornata`;
        message = `La tua richiesta di ${requestTypeText}`;
        if (dateInfo) message += ` del ${dateInfo}`;
        message += ` è stata aggiornata (${newStatusText}).`;
    }
    
    // Aggiungi le note dell'admin se presenti
    if (adminNotes && adminNotes.trim()) {
      message += ` Note: ${adminNotes}`;
    }
    
    // Dati aggiuntivi
    const data = {
      requestType: requestData.type,
      dateFrom: requestData.dateFrom || '',
      dateTo: requestData.dateTo || '',
      oldStatus,
      newStatus,
      adminNotes: adminNotes || ''
    };
    
    return await createNotification(userId, 'request_status', requestId, title, message, data);
  } catch (error) {
    console.error("createRequestStatusNotification: Errore:", error);
    throw error;
  }
};

// =============================================================================
// FUNZIONI DI UTILITÀ
// =============================================================================

/**
 * Formatta una data per le notifiche (DD/MM/YYYY)
 * @param {string} dateString - Data in formato YYYY-MM-DD
 * @returns {string} - Data formattata
 */
const formatDateForNotification = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Se è già in formato DD/MM/YYYY, restituiscila così
    if (dateString.includes('/')) {
      return dateString;
    }
    
    // Se è in formato YYYY-MM-DD, convertila
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    
    // Fallback: prova a parsare come Date
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('it-IT');
    }
    
    return dateString;
  } catch (error) {
    console.error('Errore nella formattazione della data:', error);
    return dateString;
  }
};

/**
 * Recupera tutte le richieste di un utente
 * @param {string} userId - ID dell'utente
 * @returns {Promise<Array>} - Array di richieste
 */
// Modifica alla funzione getUserLeaveRequests in firebase.js
export const getUserLeaveRequests = async (userId) => {
  try {
    if (!userId) throw new Error("userId obbligatorio");
    
    console.log("Tentativo di recuperare richieste per l'utente:", userId);
    
    const leaveRequestsRef = collection(db, "leaveRequests");
    const q = query(
      leaveRequestsRef,
      where("userId", "==", userId)
    );
    
    // Aggiungi ordinamento solo se ci sono richieste
    const querySnapshot = await getDocs(q);
    console.log(`Trovate ${querySnapshot.size} richieste`);
    
    // Converti i dati del documento in un array di oggetti
    const requests = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastUpdate: data.lastUpdate?.toDate?.() || new Date()
      };
    });
    
    // Ordina manualmente per data di creazione (più recenti prima)
    requests.sort((a, b) => b.createdAt - a.createdAt);
    
    return requests;
  } catch (error) {
    console.error("Errore dettagliato nel recupero delle richieste:", error);
    throw new Error("Impossibile recuperare le richieste: " + error.message);
  }
};

/**
 * Invia una nuova richiesta di permesso/ferie/malattia con gestione migliorata dei certificati
 * @param {Object} requestData - I dati della richiesta
 * @param {File} certificateFile - Il file del certificato (solo per malattia)
 * @returns {Promise<Object>} - I dati della richiesta inviata
 */
export const submitLeaveRequest = async (requestData) => {
  try {
    console.log("submitLeaveRequest: Tentativo di inviare una richiesta:", requestData);
    
    // Validazione dei dati (mantieni la tua validazione esistente)
    if (!requestData.type) throw new Error("Tipo di richiesta obbligatorio");
    if (!requestData.userId) throw new Error("userId è obbligatorio");

    // Validazione specifica per tipo di richiesta
    if ((requestData.type === 'permission' || requestData.type === 'vacation') && !requestData.dateFrom) {
      throw new Error("Data obbligatoria per permessi e ferie");
    }
    
    if (requestData.type === 'vacation' && !requestData.dateTo) {
      throw new Error("Data fine obbligatoria per ferie");
    }
    
    // Validazione per malattia
    if (requestData.type === 'sickness') {
      if (!requestData.dateFrom) throw new Error("Data inizio obbligatoria per malattia");
      if (!requestData.protocolCode) throw new Error("Codice protocollo obbligatorio per malattia");
    }

    // Ottieni i dati dell'utente per le notifiche
    const user = await getUserData(requestData.userId);
    if (!user) {
      throw new Error("Utente non trovato");
    }

    // Prepara l'oggetto richiesta (mantieni la tua logica esistente)
    const requestObject = {
      ...requestData,
      userEmail: user.email || "",
      userName: user.nome && user.cognome ? 
        `${user.nome} ${user.cognome}` : user.email,
      status: 'pending',
      createdAt: serverTimestamp(),
      lastUpdate: serverTimestamp()
    };

    console.log("submitLeaveRequest: Oggetto richiesta completo:", requestObject);

    // Aggiungi la richiesta alla collezione
    const requestsCollection = collection(db, "leaveRequests");
    const docRef = await addDoc(requestsCollection, requestObject);

    console.log("submitLeaveRequest: Richiesta inviata con successo, ID:", docRef.id);

    // NOVITÀ: Crea la richiesta finale con ID per le notifiche
    const finalRequest = {
      id: docRef.id,
      ...requestObject,
      createdAt: new Date(),
      lastUpdate: new Date()
    };

    // NOVITÀ: Notifica tutti gli admin della nuova richiesta
    try {
      await notifyAdminsNewRequest(finalRequest);
      console.log("submitLeaveRequest: Notifiche admin inviate con successo");
    } catch (notificationError) {
      console.error('submitLeaveRequest: Errore nella notifica admin per nuova richiesta:', notificationError);
      // Non bloccare la creazione della richiesta per errori di notifica
    }

    return finalRequest;
  } catch (error) {
    console.error("submitLeaveRequest: Errore nell'invio della richiesta:", error);
    throw error;
  }
};


/**
 * Recupera tutte le richieste (per amministratori)
 * @param {Object} filters - Filtri opzionali (status, type, dateFrom, dateTo)
 * @returns {Promise<Array>} - Array di richieste
 */
export const getAllLeaveRequests = async (filters = {}) => {
  try {
    console.log("getAllLeaveRequests: Avvio con filtri:", filters);
    
    const leaveRequestsRef = collection(db, "leaveRequests");
    
    // Costruisci la query base
    let queryConstraints = [orderBy("createdAt", "desc")];
    
    // Aggiungi filtri se presenti
    if (filters.status) {
      queryConstraints.push(where("status", "==", filters.status));
    }
    
    if (filters.type) {
      queryConstraints.push(where("type", "==", filters.type));
    }
    
    // Nota: per i filtri di data, potrebbe essere necessario implementare un'elaborazione lato client
    
    const q = query(leaveRequestsRef, ...queryConstraints);
    console.log("getAllLeaveRequests: Esecuzione query...");
    const querySnapshot = await getDocs(q);
    console.log(`getAllLeaveRequests: Trovate ${querySnapshot.size} richieste`);
    
    // Converti i dati del documento in un array di oggetti
    let requests = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastUpdate: data.lastUpdate?.toDate?.() || new Date()
      };
    });
    
    // Filtraggio per data (lato client)
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      requests = requests.filter(request => {
        const requestDate = new Date(request.dateFrom);
        return requestDate >= dateFrom;
      });
    }
    
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      requests = requests.filter(request => {
        const requestDate = new Date(request.dateFrom);
        return requestDate <= dateTo;
      });
    }
    
    console.log("getAllLeaveRequests: Restituzione richieste filtrate:", requests.length);
    return requests;
  } catch (error) {
    console.error("getAllLeaveRequests: Errore nel recupero delle richieste:", error);
    throw error;
  }
};

/**
 * Aggiorna lo stato di una richiesta
 * @param {string} requestId - ID della richiesta
 * @param {string} status - Nuovo stato ('pending', 'approved', 'rejected')
 * @param {string} adminNotes - Note dell'amministratore (opzionale)
 * @returns {Promise<Object>} - Dati aggiornati della richiesta
 */
export const updateLeaveRequestStatus = async (requestId, status, adminNotes = '') => {
  try {
    console.log(`updateLeaveRequestStatus: Aggiornamento requestId=${requestId} con status=${status}, notes=${adminNotes || 'nessuna'}`);
    
    if (!requestId) throw new Error("requestId obbligatorio");
    if (!status) throw new Error("status obbligatorio");
    
    const requestRef = doc(db, "leaveRequests", requestId);
    
    // Verifica che la richiesta esista e ottieni i dati attuali
    const requestDoc = await getDoc(requestRef);
    if (!requestDoc.exists()) {
      throw new Error(`Richiesta con ID ${requestId} non trovata`);
    }
    
    const requestData = requestDoc.data();
    const oldStatus = requestData.status;
    
    // Prepara i dati da aggiornare
    const updateData = {
      status,
      lastUpdate: serverTimestamp()
    };
    
    // Aggiungi note admin se presenti
    if (adminNotes) {
      updateData.adminNotes = adminNotes;
    }
    
    // Aggiungi info sull'approvazione/rifiuto
    if (status === 'approved' || status === 'rejected') {
      updateData.statusUpdateInfo = {
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser ? auth.currentUser.uid : 'unknown',
        previousStatus: requestData.status
      };
    }
    
    // Aggiorna lo stato della richiesta
    await updateDoc(requestRef, updateData);
    console.log(`updateLeaveRequestStatus: Status aggiornato con successo a ${status}`);
    
    // Se la richiesta è stata approvata, sincronizza con workHours
    if (status === 'approved') {
      try {
        console.log("Richiesta approvata, avvio sincronizzazione con workHours...");
        
        const syncResult = await syncApprovedRequestToWorkHours({
          ...requestData,
          status: 'approved'
        });
        
        console.log("Sincronizzazione completata:", syncResult);
        
        // Aggiungi info sulla sincronizzazione al documento
        await updateDoc(requestRef, {
          syncInfo: {
            syncedAt: serverTimestamp(),
            syncResult: syncResult.success,
            syncDetails: syncResult.details,
            syncMessage: syncResult.message
          }
        });
        
      } catch (syncError) {
        console.error("Errore durante la sincronizzazione:", syncError);
        
        // Aggiungi info sull'errore di sincronizzazione al documento
        await updateDoc(requestRef, {
          syncInfo: {
            syncedAt: serverTimestamp(),
            syncResult: false,
            syncError: syncError.message
          }
        });
        
        console.warn("Sincronizzazione fallita, ma lo stato della richiesta è stato aggiornato");
      }
    }
    
    // NUOVA PARTE: Crea notifica per l'utente
    try {
      console.log('Creazione notifica per cambio stato richiesta...');
      
      await NotificationService.notifyRequestStatusChange(
        requestData.userId,
        requestId,
        requestData,
        oldStatus,
        status,
        adminNotes
      );
      
      console.log('Notifica creata con successo');
    } catch (notificationError) {
      console.error('Errore nella creazione della notifica:', notificationError);
      // Non bloccare l'operazione principale se la notifica fallisce
    }
    
    // Restituisci i dati aggiornati
    const updatedDoc = await getDoc(requestRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data(),
      lastUpdate: new Date()
    };
    
  } catch (error) {
    console.error("updateLeaveRequestStatus: Errore nell'aggiornamento dello stato della richiesta:", error);
    throw error;
  }
};

/**
 * Elimina una richiesta e il relativo file (se presente)
 * @param {string} requestId - ID della richiesta
 * @returns {Promise<boolean>} - true se l'eliminazione è riuscita
 */
export const deleteLeaveRequest = async (requestId) => {
  try {
    console.log(`deleteLeaveRequest: Eliminazione requestId=${requestId}`);
    
    if (!requestId) throw new Error("requestId obbligatorio");
    
    const requestRef = doc(db, "leaveRequests", requestId);
    
    // Verifica che la richiesta esista
    const requestDoc = await getDoc(requestRef);
    if (!requestDoc.exists()) {
      throw new Error(`Richiesta con ID ${requestId} non trovata`);
    }
    
    // Elimina la richiesta dal database
    await deleteDoc(requestRef);
    console.log(`deleteLeaveRequest: Richiesta ${requestId} eliminata con successo`);
    
    return true;
  } catch (error) {
    console.error("deleteLeaveRequest: Errore nell'eliminazione della richiesta:", error);
    throw error;
  }
};

/**
 * Sincronizza una richiesta approvata con il sistema workHours
 * @param {Object} requestData - I dati della richiesta approvata
 * @returns {Promise<Object>} - Risultato della sincronizzazione
 */
export const syncApprovedRequestToWorkHours = async (requestData) => {
  try {
    console.log("Sincronizzazione richiesta approvata:", requestData);
    
    if (requestData.status !== 'approved') {
      console.log("Richiesta non approvata, skip sincronizzazione");
      return { success: true, message: "Richiesta non approvata, nessuna sincronizzazione necessaria" };
    }
    
    // Determina la lettera in base al tipo di richiesta
    let letterCode;
    switch (requestData.type) {
      case 'vacation':
        letterCode = 'F'; // Ferie
        break;
      case 'permission':
        letterCode = 'P'; // Permesso
        break;
      case 'sickness':
        letterCode = 'M'; // Malattia
        break;
      default:
        throw new Error(`Tipo di richiesta non riconosciuto: ${requestData.type}`);
    }
    
    // Calcola le date da segnare
    const datesToMark = [];

    if (requestData.type === 'permission') {
      if (requestData.permissionType === 'daily') {
        // Permesso giornaliero - solo la data di inizio
        datesToMark.push(requestData.dateFrom);
      } else if (requestData.permissionType === 'multi-day') {
        // Permesso multi-giorni - tutte le date dal dateFrom al dateTo (solo giorni lavorativi)
        const startDate = new Date(requestData.dateFrom);
        const endDate = new Date(requestData.dateTo);
        
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          // Salta i weekend (solo giorni lavorativi per i permessi)
          const dayOfWeek = date.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Non domenica e non sabato
            datesToMark.push(dateString);
          }
        }
      }
      // Per permessi orari, non segniamo nulla nel workHours (rimane gestione manuale)
      if (requestData.permissionType === 'hourly') {
        console.log("Permesso orario, nessuna modifica automatica al workHours");
        return { 
          success: true, 
          message: "Permesso orario approvato, gestione manuale richiesta per le ore" 
        };
      }
    } else if (requestData.type === 'vacation') {
      // Per le ferie, segna tutte le date dal dateFrom al dateTo (solo giorni lavorativi)
      const startDate = new Date(requestData.dateFrom);
      const endDate = new Date(requestData.dateTo);
      
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        // Salta i weekend (opzionale, dipende dalla policy aziendale)
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Non domenica e non sabato
          datesToMark.push(dateString);
        }
      }
    } else if (requestData.type === 'sickness') {
      // Per la malattia, segna la data di inizio
      datesToMark.push(requestData.dateFrom);
      
      // Se c'è una data di fine, aggiungi tutte le date nel range
      if (requestData.dateTo) {
        const startDate = new Date(requestData.dateFrom);
        const endDate = new Date(requestData.dateTo);
        
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          if (!datesToMark.includes(dateString)) {
            datesToMark.push(dateString);
          }
        }
      }
    }
    
    console.log(`Date da segnare con ${letterCode}:`, datesToMark);
    
    if (datesToMark.length === 0) {
      return { success: true, message: "Nessuna data da segnare" };
    }
    
    // Raggruppa le date per mese/anno per ottimizzare le operazioni
    const datesByMonth = {};
    
    datesToMark.forEach(dateString => {
      const [year, month, day] = dateString.split('-');
      const monthKey = `${requestData.userId}_${month.replace(/^0+/, '')}_${year}`;
      
      if (!datesByMonth[monthKey]) {
        datesByMonth[monthKey] = {
          userId: requestData.userId,
          month: month.replace(/^0+/, ''),
          year,
          dates: []
        };
      }
      
      datesByMonth[monthKey].dates.push(dateString);
    });
    
    // Aggiorna ogni mese
    const updateResults = [];
    
    for (const [monthKey, monthData] of Object.entries(datesByMonth)) {
      try {
        const result = await updateWorkHoursForApprovedRequest(
          monthData.userId,
          monthData.month,
          monthData.year,
          monthData.dates,
          letterCode,
          requestData.type
        );
        
        updateResults.push({
          monthKey,
          success: true,
          ...result
        });
      } catch (error) {
        console.error(`Errore aggiornamento mese ${monthKey}:`, error);
        updateResults.push({
          monthKey,
          success: false,
          error: error.message
        });
      }
    }
    
    // Verifica risultati
    const successfulUpdates = updateResults.filter(r => r.success);
    const failedUpdates = updateResults.filter(r => !r.success);
    
    let message = `Sincronizzazione completata: ${successfulUpdates.length} mesi aggiornati`;
    if (failedUpdates.length > 0) {
      message += `, ${failedUpdates.length} errori`;
    }
    
    return {
      success: failedUpdates.length === 0,
      message,
      details: {
        letterCode,
        totalDates: datesToMark.length,
        successfulUpdates: successfulUpdates.length,
        failedUpdates: failedUpdates.length,
        updateResults
      }
    };
    
  } catch (error) {
    console.error("Errore nella sincronizzazione richiesta approvata:", error);
    throw error;
  }
};

/**
 * Aggiorna workHours per le date di una richiesta approvata
 * VERSIONE CORRETTA: Crea entry se non esistono
 * @param {string} userId - ID dell'utente
 * @param {string} month - Mese (senza zero iniziale)
 * @param {string} year - Anno
 * @param {Array} dates - Array delle date da segnare
 * @param {string} letterCode - Lettera da usare (F, P, M)
 * @param {string} requestType - Tipo di richiesta per le note
 * @returns {Promise<Object>} - Risultato dell'aggiornamento
 */
const updateWorkHoursForApprovedRequest = async (userId, month, year, dates, letterCode, requestType) => {
  try {
    const workHoursId = `${userId}_${month}_${year}`;
    const workHoursRef = doc(db, "workHours", workHoursId);
    
    console.log(`Aggiornamento workHours: ${workHoursId} per date:`, dates);
    
    // Genera il calendario completo del mese se il documento non esiste
    const generateCompleteMonthCalendar = (month, year) => {
      const monthInt = parseInt(month);
      const yearInt = parseInt(year);
      const daysInMonth = new Date(yearInt, monthInt, 0).getDate();
      const entries = [];
      const dayNames = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(yearInt, monthInt - 1, day);
        const dayOfWeek = date.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        const dateStr = `${yearInt}-${monthInt.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        entries.push({
          date: dateStr,
          day: dayNames[dayOfWeek],
          total: 0,
          overtime: 0,
          notes: "",
          isWeekend: isWeekend,
          hasData: false
        });
      }
      
      return entries.sort((a, b) => new Date(a.date) - new Date(b.date));
    };
    
    // Ottieni il documento esistente o creane uno nuovo
    const workHoursSnap = await getDoc(workHoursRef);
    let entries = [];
    let documentExists = workHoursSnap.exists();
    
    if (documentExists) {
      const workHoursData = workHoursSnap.data();
      entries = workHoursData.entries || [];
      console.log(`Documento esistente trovato con ${entries.length} entries`);
    } else {
      // Crea il calendario completo se il documento non esiste
      entries = generateCompleteMonthCalendar(month, year);
      console.log(`Documento non esistente, creato calendario con ${entries.length} entries`);
    }
    
    // Determina la descrizione per le note
    let noteDescription;
    switch (requestType) {
      case 'vacation':
        noteDescription = 'Ferie approvate';
        break;
      case 'permission':
        noteDescription = 'Permesso approvato';
        break;
      case 'sickness':
        noteDescription = 'Malattia approvata';
        break;
      default:
        noteDescription = 'Richiesta approvata';
    }
    
    // *** QUESTA È LA PARTE FONDAMENTALE CORRETTA ***
    let updatedCount = 0;
    let createdCount = 0;
    let conflictCount = 0;
    const conflicts = [];
    
    dates.forEach(dateToMark => {
      let entryIndex = entries.findIndex(entry => entry.date === dateToMark);
      
      if (entryIndex >= 0) {
        // Entry esistente trovata
        const existingEntry = entries[entryIndex];
        
        // Controlla se c'è già un valore diverso da 0 o vuoto
        const hasConflict = existingEntry.total !== 0 && 
                           existingEntry.total !== '' && 
                           existingEntry.total !== letterCode &&
                           !['M', 'P', 'F', 'A'].includes(existingEntry.total);
        
        if (hasConflict) {
          console.warn(`Conflitto rilevato per ${dateToMark}: valore esistente = ${existingEntry.total}`);
          conflicts.push({
            date: dateToMark,
            existingValue: existingEntry.total,
            newValue: letterCode
          });
          conflictCount++;
          
          // Sovrascrivi comunque (le richieste approvate hanno priorità)
        }
        
        // Aggiorna l'entry esistente
        entries[entryIndex] = {
          ...existingEntry,
          total: letterCode,
          overtime: 0, // Reset straordinario per giorni speciali
          notes: `${noteDescription} - ${new Date().toLocaleDateString('it-IT')}`,
          hasData: true
        };
        
        updatedCount++;
        console.log(`Entry aggiornata per data: ${dateToMark}`);
        
      } else {
        // *** ENTRY NON TROVATA: CREALA ***
        console.log(`Entry non trovata per data: ${dateToMark}, creazione nuova entry...`);
        
        // Calcola il giorno della settimana
        const date = new Date(dateToMark);
        const dayNames = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
        const dayOfWeek = date.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        // Crea nuova entry
        const newEntry = {
          date: dateToMark,
          day: dayNames[dayOfWeek],
          total: letterCode,
          overtime: 0,
          notes: `${noteDescription} - ${new Date().toLocaleDateString('it-IT')}`,
          isWeekend: isWeekend,
          hasData: true
        };
        
        // Aggiungi la nuova entry all'array
        entries.push(newEntry);
        createdCount++;
        updatedCount++;
        
        console.log(`Nuova entry creata per data: ${dateToMark}`);
      }
    });
    
    // Ordina le entries per data per mantenere l'ordine cronologico
    entries.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Salva il documento aggiornato
    if (documentExists) {
      await updateDoc(workHoursRef, {
        entries,
        lastUpdated: serverTimestamp()
      });
      console.log(`Documento esistente aggiornato`);
    } else {
      await setDoc(workHoursRef, {
        userId,
        month,
        year,
        entries,
        lastUpdated: serverTimestamp(),
        createdBy: 'approved_request_sync'
      });
      console.log(`Nuovo documento creato`);
    }
    
    console.log(`Aggiornamento completato: ${updatedCount} date segnate con ${letterCode} (${createdCount} nuove entry create)`);
    
    return {
      updatedCount,
      createdCount,
      conflictCount,
      conflicts,
      letterCode,
      noteDescription
    };
    
  } catch (error) {
    console.error("Errore nell'aggiornamento workHours:", error);
    throw error;
  }
};

/**
 * SOSTITUISCE la funzione updateLeaveRequestStatus esistente
 * Aggiorna lo stato di una richiesta con sincronizzazione automatica
 */
export const updateLeaveRequestStatusWithSync = async (requestId, status, adminNotes = '') => {
  try {
    console.log(`updateLeaveRequestStatus: Aggiornamento requestId=${requestId} con status=${status}, notes=${adminNotes || 'nessuna'}`);
    
    if (!requestId) throw new Error("requestId obbligatorio");
    if (!status) throw new Error("status obbligatorio");
    
    const requestRef = doc(db, "leaveRequests", requestId);
    
    // Verifica che la richiesta esista e ottieni i dati attuali
    const requestDoc = await getDoc(requestRef);
    if (!requestDoc.exists()) {
      throw new Error(`Richiesta con ID ${requestId} non trovata`);
    }
    
    const requestData = requestDoc.data();
    const oldStatus = requestData.status;
    
    // Prepara i dati da aggiornare
    const updateData = {
      status,
      lastUpdate: serverTimestamp()
    };
    
    // Aggiungi note admin se presenti
    if (adminNotes) {
      updateData.adminNotes = adminNotes;
    }
    
    // Aggiungi info sull'approvazione/rifiuto
    if (status === 'approved' || status === 'rejected') {
      updateData.statusUpdateInfo = {
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser ? auth.currentUser.uid : 'unknown',
        previousStatus: requestData.status
      };
    }
    
    // Aggiorna lo stato della richiesta
    await updateDoc(requestRef, updateData);
    console.log(`updateLeaveRequestStatus: Status aggiornato con successo a ${status}`);
    
    // Se la richiesta è stata approvata, sincronizza con workHours
    if (status === 'approved') {
      try {
        console.log("Richiesta approvata, avvio sincronizzazione con workHours...");
        
        const syncResult = await syncApprovedRequestToWorkHours({
          ...requestData,
          status: 'approved'
        });
        
        console.log("Sincronizzazione completata:", syncResult);
        
        // Aggiungi info sulla sincronizzazione al documento
        await updateDoc(requestRef, {
          syncInfo: {
            syncedAt: serverTimestamp(),
            syncResult: syncResult.success,
            syncDetails: syncResult.details,
            syncMessage: syncResult.message
          }
        });
        
      } catch (syncError) {
        console.error("Errore durante la sincronizzazione:", syncError);
        
        // Aggiungi info sull'errore di sincronizzazione al documento
        await updateDoc(requestRef, {
          syncInfo: {
            syncedAt: serverTimestamp(),
            syncResult: false,
            syncError: syncError.message
          }
        });
        
        console.warn("Sincronizzazione fallita, ma lo stato della richiesta è stato aggiornato");
      }
    }
    
    // NUOVA PARTE: Crea notifica per l'utente
    try {
      console.log('Creazione notifica per cambio stato richiesta...');
      
      await NotificationService.notifyRequestStatusChange(
        requestData.userId,
        requestId,
        requestData,
        oldStatus,
        status,
        adminNotes
      );
      
      console.log('Notifica creata con successo');
    } catch (notificationError) {
      console.error('Errore nella creazione della notifica:', notificationError);
      // Non bloccare l'operazione principale se la notifica fallisce
    }
    
    // Restituisci i dati aggiornati
    const updatedDoc = await getDoc(requestRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data(),
      lastUpdate: new Date()
    };
    
  } catch (error) {
    console.error("updateLeaveRequestStatus: Errore nell'aggiornamento dello stato della richiesta:", error);
    throw error;
  }
};

// src/firebase.js - Aggiungi queste nuove funzioni

/**
 * Elimina una richiesta approvata e desincronizza i dati dal workHours
 * @param {string} requestId - ID della richiesta da eliminare
 * @returns {Promise<Object>} - Risultato dell'operazione
 */
export const deleteApprovedRequestWithDesync = async (requestId) => {
  try {
    console.log(`deleteApprovedRequestWithDesync: Avvio eliminazione per requestId=${requestId}`);
    
    if (!requestId) throw new Error("requestId obbligatorio");
    
    const requestRef = doc(db, "leaveRequests", requestId);
    
    // Verifica che la richiesta esista
    const requestDoc = await getDoc(requestRef);
    if (!requestDoc.exists()) {
      throw new Error(`Richiesta con ID ${requestId} non trovata`);
    }
    
    const requestData = requestDoc.data();
    console.log("Dati richiesta da eliminare:", requestData);
    
    // Solo le richieste approvate possono essere eliminate con desincronizzazione
    if (requestData.status !== 'approved') {
      throw new Error("Solo le richieste approvate possono essere eliminate con desincronizzazione");
    }
    
    // Esegui la desincronizzazione dal workHours PRIMA di eliminare la richiesta
    let desyncResult = null;
    
    try {
      console.log("Avvio desincronizzazione dal workHours...");
      desyncResult = await desyncApprovedRequestFromWorkHours(requestData);
      console.log("Desincronizzazione completata:", desyncResult);
    } catch (desyncError) {
      console.error("Errore durante la desincronizzazione:", desyncError);
      // Non bloccare l'eliminazione, ma registra l'errore
      desyncResult = {
        success: false,
        message: `Errore desincronizzazione: ${desyncError.message}`,
        error: desyncError.message
      };
    }
    
    // Elimina la richiesta dal database
    await deleteDoc(requestRef);
    console.log(`Richiesta ${requestId} eliminata con successo`);
    
    // Elimina il file associato se presente (per malattie)
    if (requestData.fileInfo && requestData.fileInfo.fileStoragePath) {
      try {
        console.log(`Eliminazione file: ${requestData.fileInfo.fileStoragePath}`);
        const fileRef = storageRef(firebaseStorage, requestData.fileInfo.fileStoragePath);
        await storageDeleteObject(fileRef);
        console.log("File eliminato con successo");
      } catch (fileError) {
        console.error("Errore nell'eliminazione del file:", fileError);
        // Non bloccare l'operazione principale
      }
    }
    
    return {
      success: true,
      message: "Richiesta eliminata con successo",
      requestData,
      desyncResult,
      deletedFiles: requestData.fileInfo ? [requestData.fileInfo.fileStoragePath] : []
    };
    
  } catch (error) {
    console.error("deleteApprovedRequestWithDesync: Errore:", error);
    throw error;
  }
};

// =============================================================================
// FUNZIONI PER IL RUOLO RESPONSABILE
// =============================================================================

/**
 * Aggiorna il ruolo di un utente con supporto per il ruolo "responsabile"
 * @param {string} userId - ID dell'utente
 * @param {string} newRole - Nuovo ruolo ('user', 'responsabile', 'admin')
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    if (!userId || !newRole) throw new Error("userId e newRole sono obbligatori");
    
    // Validazione del ruolo
    const validRoles = ['user', 'responsabile', 'admin', 'hr'];
    if (!validRoles.includes(newRole)) {
      throw new Error(`Ruolo non valido: ${newRole}. Ruoli consentiti: ${validRoles.join(', ')}`);
    }
    
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      role: newRole,
      updatedAt: serverTimestamp()
    });
    
    console.log(`updateUserRole: Ruolo aggiornato per utente ${userId}: ${newRole}`);
    return true;
  } catch (error) {
    console.error("updateUserRole: Errore nell'aggiornamento del ruolo:", error);
    throw error;
  }
};

/**
 * Ottieni tutti gli utenti con ruolo responsabile
 * @returns {Promise<Array>} - Array di utenti responsabili
 */
export const getResponsabili = async () => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "responsabile"));
    const querySnapshot = await getDocs(q);
    
    const responsabili = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`getResponsabili: Trovati ${responsabili.length} responsabili`);
    return responsabili;
  } catch (error) {
    console.error("getResponsabili: Errore nel recupero dei responsabili:", error);
    throw error;
  }
};

/**
 * Ottieni tutti gli utenti con ruolo admin o responsabile
 * @returns {Promise<Array>} - Array di utenti con privilegi elevati
 */

export const getAdminAndResponsabili = async () => {
  try {
    const usersRef = collection(db, "users");
    const adminQuery = query(usersRef, where("role", "==", "admin"));
    const responsabileQuery = query(usersRef, where("role", "==", "responsabile"));
    const hrQuery = query(usersRef, where("role", "==", "hr"));
    
    const [adminSnapshot, responsabileSnapshot, hrSnapshot] = await Promise.all([
      getDocs(adminQuery),
      getDocs(responsabileQuery),
      getDocs(hrQuery)
    ]);
    
    const users = [];
    
    adminSnapshot.docs.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    responsabileSnapshot.docs.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    hrSnapshot.docs.forEach(doc => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`getAdminAndResponsabili: Trovati ${users.length} utenti con privilegi elevati`);
    return users;
  } catch (error) {
    console.error("getAdminAndResponsabili: Errore nel recupero degli utenti:", error);
    throw error;
  }
};

/**
 * Aggiorna le ore lavorative con tracciamento delle modifiche per responsabili
 * @param {string} userId - ID dell'utente
 * @param {string} month - Mese
 * @param {string} year - Anno
 * @param {Array} entries - Array delle ore aggiornate
 * @param {string} modifiedBy - ID del responsabile che effettua la modifica
 * @param {string} modifierName - Nome del responsabile
 */
export const updateWorkHoursWithTracking = async (userId, month, year, entries, modifiedBy, modifierName) => {
  try {
    if (!userId || !month || !year || !entries || !modifiedBy || !modifierName) {
      throw new Error("Tutti i parametri sono obbligatori");
    }
    
    // Normalizza il mese (rimuovi zeri iniziali)
    const normalizedMonth = month.toString().replace(/^0+/, '') || month;
    
    const workHoursRef = doc(db, "workHours", `${userId}_${normalizedMonth}_${year}`);
    
    // Ottieni i dati esistenti per confronto
    const existingDoc = await getDoc(workHoursRef);
    const existingEntries = existingDoc.exists() ? existingDoc.data().entries || [] : [];
    
    // Traccia le modifiche per ogni entry
    const trackedEntries = entries.map((entry, index) => {
      const existingEntry = existingEntries[index] || {};
      const isModified = JSON.stringify(existingEntry) !== JSON.stringify(entry);
      
      const baseEntry = {
        ...entry,
        date: entry.date || `${year}-${normalizedMonth.padStart(2, '0')}-${(index + 1).toString().padStart(2, '0')}`
      };
      
      if (isModified) {
        // Aggiungi informazioni di tracciamento solo se c'è stata una modifica
        const modification = {
          timestamp: new Date().toISOString(),
          modifiedBy: modifiedBy,
          modifierName: modifierName,
          oldValues: existingEntry,
          newValues: entry
        };
        
        return {
          ...baseEntry,
          lastModified: new Date().toISOString(),
          modifiedBy: modifiedBy,
          modifierName: modifierName,
          modifications: [...(existingEntry.modifications || []), modification]
        };
      }
      
      return baseEntry;
    });
    
    // Salva nel database
    await setDoc(workHoursRef, {
      userId,
      month: normalizedMonth,
      year: parseInt(year),
      entries: trackedEntries,
      lastUpdate: serverTimestamp(),
      lastModifiedBy: modifiedBy,
      lastModifierName: modifierName
    }, { merge: true });
    
    console.log(`updateWorkHoursWithTracking: Ore aggiornate dal responsabile ${modifierName} per utente ${userId}`);
    return { success: true, entriesUpdated: trackedEntries.length };
  } catch (error) {
    console.error("updateWorkHoursWithTracking: Errore nell'aggiornamento delle ore:", error);
    throw error;
  }
};

/**
 * Crea una richiesta permesso/ferie per un operaio (funzione per responsabili)
 * @param {Object} requestData - Dati della richiesta
 * @param {string} responsableId - ID del responsabile che effettua la richiesta
 * @param {string} responsableName - Nome del responsabile
 */
export const createRequestForWorker = async (requestData, responsableId, responsableName) => {
  try {
    if (!requestData || !responsableId || !responsableName) {
      throw new Error("Tutti i parametri sono obbligatori");
    }
    
    if (!requestData.userId || !requestData.type || !requestData.startDate || !requestData.endDate) {
      throw new Error("Dati della richiesta incompleti");
    }
    
    const requestsRef = collection(db, "leaveRequests");
    
    const newRequest = {
      userId: requestData.userId, // ID dell'operaio per cui si fa la richiesta
      userEmail: requestData.userEmail || '',
      workerName: requestData.workerName || 'Operaio',
      type: requestData.type,
      startDate: requestData.startDate,
      endDate: requestData.endDate,
      reason: requestData.reason || '',
      urgent: requestData.urgent || false,
      
      // Informazioni sul responsabile che crea la richiesta
      requestedBy: responsableId,
      requestedByName: responsableName,
      requestedByRole: 'responsabile',
      
      // Flag per identificare le richieste proxy
      isProxyRequest: true,
      
      // Stati e timestamp
      status: 'pending',
      createdAt: serverTimestamp(),
      lastUpdate: serverTimestamp(),
      
      // Note aggiuntive
      notes: `Richiesta creata dal responsabile ${responsableName} per conto di ${requestData.workerName}`
    };
    
    const docRef = await addDoc(requestsRef, newRequest);
    console.log(`createRequestForWorker: Richiesta creata dal responsabile ${responsableName} per ${requestData.workerName}`);
    
    // Restituisci la richiesta con l'ID
    const createdRequest = { 
      id: docRef.id, 
      ...newRequest,
      createdAt: new Date() // Converti per compatibilità
    };
    
    // Notifica gli admin della nuova richiesta
    try {
      await notifyAdminsNewRequest(createdRequest, requestData.workerName);
    } catch (notificationError) {
      console.error("createRequestForWorker: Errore nella notifica agli admin:", notificationError);
      // Non bloccare l'operazione principale
    }
    
    return createdRequest;
  } catch (error) {
    console.error("createRequestForWorker: Errore nella creazione della richiesta:", error);
    throw error;
  }
};

/**
 * Crea una timbratura manuale con tracciamento del responsabile
 * @param {string} userId - ID dell'utente per cui creare la timbratura
 * @param {string} responsableId - ID del responsabile
 * @param {string} responsableName - Nome del responsabile
 * @param {Object} options - Opzioni aggiuntive
 */
export const createManualTimestamp = async (userId, responsableId, responsableName, options = {}) => {
  try {
    if (!userId || !responsableId || !responsableName) {
      throw new Error("userId, responsableId e responsableName sono obbligatori");
    }
    
    const timestampsRef = collection(db, "timestamps");
    
    const timestampData = {
      userId: userId,
      timestamp: serverTimestamp(),
      location: options.location || "Responsabile - Timbratura Manuale",
      recordedBy: responsableId,
      recordedByName: responsableName,
      recordedByRole: 'responsabile',
      isManualEntry: true,
      notes: options.notes || `Timbratura effettuata dal responsabile ${responsableName}`,
      ...options.additionalData
    };
    
    const docRef = await addDoc(timestampsRef, timestampData);
    console.log(`createManualTimestamp: Timbratura manuale creata dal responsabile ${responsableName} per utente ${userId}`);
    
    return { 
      id: docRef.id, 
      ...timestampData,
      timestamp: new Date() // Converti per compatibilità
    };
  } catch (error) {
    console.error("createManualTimestamp: Errore nella creazione della timbratura manuale:", error);
    throw error;
  }
};

/**
 * Crea una timbratura QR con tracciamento del responsabile
 * @param {string} userId - ID dell'utente per cui creare la timbratura
 * @param {string} qrCode - Codice QR scansionato
 * @param {string} responsableId - ID del responsabile
 * @param {string} responsableName - Nome del responsabile
 * @param {Object} options - Opzioni aggiuntive
 */
export const createQRTimestamp = async (userId, qrCode, responsableId, responsableName, options = {}) => {
  try {
    if (!userId || !qrCode || !responsableId || !responsableName) {
      throw new Error("Tutti i parametri sono obbligatori");
    }
    
    const timestampsRef = collection(db, "timestamps");
    
    const timestampData = {
      userId: userId,
      timestamp: serverTimestamp(),
      location: options.location || "Responsabile - Scanner QR",
      recordedBy: responsableId,
      recordedByName: responsableName,
      recordedByRole: 'responsabile',
      isQREntry: true,
      qrCode: qrCode,
      notes: options.notes || `Timbratura QR effettuata dal responsabile ${responsableName}`,
      ...options.additionalData
    };
    
    const docRef = await addDoc(timestampsRef, timestampData);
    console.log(`createQRTimestamp: Timbratura QR creata dal responsabile ${responsableName} per utente ${userId}`);
    
    return { 
      id: docRef.id, 
      ...timestampData,
      timestamp: new Date() // Converti per compatibilità
    };
  } catch (error) {
    console.error("createQRTimestamp: Errore nella creazione della timbratura QR:", error);
    throw error;
  }
};

/**
 * Ottieni le timbrature recenti (ultimi N record)
 * @param {number} limit - Numero massimo di timbrature da recuperare (default: 10)
 * @returns {Promise<Array>} - Array di timbrature
 */
export const getRecentTimestamps = async (limit = 10) => {
  try {
    const timestampsRef = collection(db, "timestamps");
    const q = query(timestampsRef, orderBy("timestamp", "desc"), limit(limit));
    const querySnapshot = await getDocs(q);
    
    const timestamps = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    
    console.log(`getRecentTimestamps: Recuperate ${timestamps.length} timbrature recenti`);
    return timestamps;
  } catch (error) {
    console.error("getRecentTimestamps: Errore nel recupero delle timbrature:", error);
    throw error;
  }
};

/**
 * Trova un utente tramite email o ID
 * @param {string} identifier - Email o ID dell'utente
 * @returns {Promise<Object|null>} - Dati dell'utente o null se non trovato
 */
export const findUserByIdentifier = async (identifier) => {
  try {
    if (!identifier) throw new Error("Identifier è obbligatorio");
    
    // Prima prova a cercare per ID
    try {
      const userDoc = await getDoc(doc(db, "users", identifier));
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
    } catch (error) {
      // ID non valido, continua con la ricerca per email
    }
    
    // Cerca per email
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", identifier));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    
    console.log(`findUserByIdentifier: Utente non trovato per identifier: ${identifier}`);
    return null;
  } catch (error) {
    console.error("findUserByIdentifier: Errore nella ricerca utente:", error);
    throw error;
  }
};

/**
 * Verifica se un utente ha il ruolo responsabile
 * @param {string} userId - ID dell'utente
 * @returns {Promise<boolean>} - True se l'utente è un responsabile
 */
export const isResponsabile = async (userId) => {
  try {
    if (!userId) return false;
    
    const userData = await getUserData(userId);
    return userData && userData.role === 'responsabile';
  } catch (error) {
    console.error("isResponsabile: Errore nella verifica del ruolo:", error);
    return false;
  }
};

/**
 * Verifica se un utente ha privilegi elevati (admin o responsabile)
 * @param {string} userId - ID dell'utente
 * @returns {Promise<boolean>} - True se l'utente ha privilegi elevati
 */
export const hasElevatedPrivileges = async (userId) => {
  try {
    if (!userId) return false;
    
    const userData = await getUserData(userId);
    return userData && (userData.role === 'admin' || userData.role === 'responsabile');
  } catch (error) {
    console.error("hasElevatedPrivileges: Errore nella verifica dei privilegi:", error);
    return false;
  }
};

// =============================================================================
// FINE FUNZIONI RESPONSABILE
// =============================================================================

/**
 * Verifica se un utente ha il ruolo HR
 * @param {string} userId - ID dell'utente
 * @returns {Promise<boolean>} - True se l'utente è HR
 */
export const isHR = async (userId) => {
  try {
    if (!userId) return false;
    
    const userData = await getUserData(userId);
    return userData && userData.role === 'hr';
  } catch (error) {
    console.error("isHR: Errore nella verifica del ruolo:", error);
    return false;
  }
};

/**
 * Ottieni tutti gli utenti con ruolo HR
 * @returns {Promise<Array>} - Array di utenti HR
 */
export const getHRUsers = async () => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "hr"));
    const querySnapshot = await getDocs(q);
    
    const hrUsers = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`getHRUsers: Trovati ${hrUsers.length} utenti HR`);
    return hrUsers;
  } catch (error) {
    console.error("getHRUsers: Errore nel recupero degli utenti HR:", error);
    throw error;
  }
};

/**
 * Verifica se un utente ha privilegi amministrativi (admin o hr)
 * @param {string} userId - ID dell'utente
 * @returns {Promise<boolean>} - True se l'utente ha privilegi amministrativi
 */
export const hasAdminPrivileges = async (userId) => {
  try {
    if (!userId) return false;
    
    const userData = await getUserData(userId);
    return userData && (userData.role === 'admin' || userData.role === 'hr');
  } catch (error) {
    console.error("hasAdminPrivileges: Errore nella verifica dei privilegi:", error);
    return false;
  }
};

console.log("Firebase.js: Funzioni per il ruolo responsabile caricate con successo");

/**
 * Desincronizza una richiesta approvata dal workHours
 * @param {Object} requestData - Dati della richiesta da desincronizzare
 * @returns {Promise<Object>} - Risultato della desincronizzazione
 */
const desyncApprovedRequestFromWorkHours = async (requestData) => {
  try {
    console.log("desyncApprovedRequestFromWorkHours: Avvio per richiesta:", requestData);
    
    if (!requestData.userId) {
      throw new Error("userId mancante nei dati della richiesta");
    }
    
    // Determina le date da desincronizzare
    const datesToDesync = [];
    
    if (requestData.type === 'permission') {
      if (requestData.permissionType === 'daily') {
        // Permesso giornaliero - solo la data di inizio
        datesToDesync.push(requestData.dateFrom);
      } else if (requestData.permissionType === 'multi-day') {
        // Permesso multi-giorni - tutte le date dal dateFrom al dateTo (solo giorni lavorativi)
        const startDate = new Date(requestData.dateFrom);
        const endDate = new Date(requestData.dateTo);
        
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          // Salta i weekend (solo giorni lavorativi per i permessi)
          const dayOfWeek = date.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Non domenica e non sabato
            datesToDesync.push(dateString);
          }
        }
      } else if (requestData.permissionType === 'hourly') {
        // Permesso orario - nessuna desincronizzazione automatica
        console.log("Permesso orario, nessuna desincronizzazione automatica necessaria");
        return {
          success: true,
          message: "Permesso orario: nessuna desincronizzazione automatica necessaria",
          datesDesynchronized: 0
        };
      }
    } else if (requestData.type === 'vacation') {
      // Ferie - tutte le date dal dateFrom al dateTo (solo giorni lavorativi)
      const startDate = new Date(requestData.dateFrom);
      const endDate = new Date(requestData.dateTo);
      
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        // Salta i weekend (opzionale, dipende dalla policy aziendale)
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Non domenica e non sabato
          datesToDesync.push(dateString);
        }
      }
    } else if (requestData.type === 'sickness') {
      // Malattia - segna la data di inizio
      datesToDesync.push(requestData.dateFrom);
      
      // Se c'è una data di fine, aggiungi tutte le date nel range
      if (requestData.dateTo) {
        const startDate = new Date(requestData.dateFrom);
        const endDate = new Date(requestData.dateTo);
        
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          if (!datesToDesync.includes(dateString)) {
            datesToDesync.push(dateString);
          }
        }
      }
    }
    
    console.log(`Date da desincronizzare:`, datesToDesync);
    
    if (datesToDesync.length === 0) {
      return {
        success: true,
        message: "Nessuna data da desincronizzare",
        datesDesynchronized: 0
      };
    }
    
    // Raggruppa le date per mese/anno per ottimizzare le operazioni
    const datesByMonth = {};
    
    datesToDesync.forEach(dateString => {
      const [year, month, day] = dateString.split('-');
      const monthKey = `${requestData.userId}_${month.replace(/^0+/, '')}_${year}`;
      
      if (!datesByMonth[monthKey]) {
        datesByMonth[monthKey] = {
          userId: requestData.userId,
          month: month.replace(/^0+/, ''),
          year,
          dates: []
        };
      }
      
      datesByMonth[monthKey].dates.push(dateString);
    });
    
    // Desincronizza ogni mese
    const desyncResults = [];
    
    for (const [monthKey, monthData] of Object.entries(datesByMonth)) {
      try {
        const result = await desyncWorkHoursForDates(
          monthData.userId,
          monthData.month,
          monthData.year,
          monthData.dates,
          requestData.type
        );
        
        desyncResults.push({
          monthKey,
          success: true,
          ...result
        });
      } catch (error) {
        console.error(`Errore desincronizzazione mese ${monthKey}:`, error);
        desyncResults.push({
          monthKey,
          success: false,
          error: error.message
        });
      }
    }
    
    // Verifica risultati
    const successfulDesyncs = desyncResults.filter(r => r.success);
    const failedDesyncs = desyncResults.filter(r => !r.success);
    
    const totalDesynchronized = successfulDesyncs.reduce((sum, result) => sum + (result.desynchronizedCount || 0), 0);
    
    let message = `Desincronizzazione completata: ${totalDesynchronized} date ripristinate`;
    if (failedDesyncs.length > 0) {
      message += `, ${failedDesyncs.length} errori`;
    }
    
    return {
      success: failedDesyncs.length === 0,
      message,
      datesDesynchronized: totalDesynchronized,
      details: {
        totalDates: datesToDesync.length,
        successfulDesyncs: successfulDesyncs.length,
        failedDesyncs: failedDesyncs.length,
        desyncResults
      }
    };
    
  } catch (error) {
    console.error("Errore nella desincronizzazione richiesta:", error);
    throw error;
  }
};

/**
 * Desincronizza le date specificate dal workHours
 * @param {string} userId - ID dell'utente
 * @param {string} month - Mese (senza zero iniziale)
 * @param {string} year - Anno
 * @param {Array} dates - Array delle date da desincronizzare
 * @param {string} requestType - Tipo di richiesta per le note
 * @returns {Promise<Object>} - Risultato della desincronizzazione
 */
const desyncWorkHoursForDates = async (userId, month, year, dates, requestType) => {
  try {
    const workHoursId = `${userId}_${month}_${year}`;
    const workHoursRef = doc(db, "workHours", workHoursId);
    
    console.log(`Desincronizzazione workHours: ${workHoursId} per date:`, dates);
    
    // Ottieni il documento esistente
    const workHoursSnap = await getDoc(workHoursRef);
    
    if (!workHoursSnap.exists()) {
      console.log(`Documento workHours ${workHoursId} non esiste, nessuna desincronizzazione necessaria`);
      return {
        desynchronizedCount: 0,
        message: "Documento workHours non trovato, nessuna desincronizzazione necessaria"
      };
    }
    
    const workHoursData = workHoursSnap.data();
    let entries = workHoursData.entries || [];
    
    console.log(`Documento workHours trovato con ${entries.length} entries`);
    
    // Determina i valori di richiesta approvata da cercare nelle note
    const approvedNoteKeywords = [
      'approvata',
      'ferie approvate',
      'permesso approvato', 
      'malattia approvata'
    ];
    
    let desynchronizedCount = 0;
    const conflicts = [];
    
    // Desincronizza le entries per le date specificate
    dates.forEach(dateToDesync => {
      const entryIndex = entries.findIndex(entry => entry.date === dateToDesync);
      
      if (entryIndex >= 0) {
        const existingEntry = entries[entryIndex];
        
        // Controlla se questa entry è stata creata da una richiesta approvata
        const isFromApprovedRequest = existingEntry.notes && 
          approvedNoteKeywords.some(keyword => 
            existingEntry.notes.toLowerCase().includes(keyword.toLowerCase())
          );
        
        // Controlla se il valore corrisponde a una lettera speciale (M, P, F)
        const isSpecialLetter = ['M', 'P', 'F'].includes(existingEntry.total);
        
        if (isFromApprovedRequest || isSpecialLetter) {
          console.log(`Desincronizzando entry per ${dateToDesync}: ${JSON.stringify(existingEntry)}`);
          
          // Ripristina l'entry ai valori predefiniti (giornata vuota)
          const dayOfWeek = new Date(dateToDesync).toLocaleDateString('it-IT', { weekday: 'long' });
          const isWeekend = [0, 6].includes(new Date(dateToDesync).getDay());
          
          entries[entryIndex] = {
            date: dateToDesync,
            day: dayOfWeek,
            total: 0,
            overtime: 0,
            notes: "",
            isWeekend,
            hasData: false
          };
          
          desynchronizedCount++;
        } else {
          console.warn(`Entry per ${dateToDesync} non sembra essere da richiesta approvata, saltata`);
          conflicts.push({
            date: dateToDesync,
            existingValue: existingEntry.total,
            existingNotes: existingEntry.notes,
            reason: "Non creata da richiesta approvata"
          });
        }
      } else {
        console.warn(`Entry non trovata per data: ${dateToDesync}`);
        conflicts.push({
          date: dateToDesync,
          reason: "Entry non trovata"
        });
      }
    });
    
    // Salva il documento aggiornato se ci sono state modifiche
    if (desynchronizedCount > 0) {
      await updateDoc(workHoursRef, {
        entries,
        lastUpdated: serverTimestamp(),
        lastDesynchronization: {
          date: serverTimestamp(),
          requestType,
          datesDesynchronized: dates,
          desynchronizedCount
        }
      });
      
      console.log(`Desincronizzazione completata: ${desynchronizedCount} date ripristinate`);
    }
    
    return {
      desynchronizedCount,
      conflicts,
      message: `${desynchronizedCount} date desincronizzate con successo`
    };
    
  } catch (error) {
    console.error("Errore nella desincronizzazione workHours:", error);
    throw error;
  }
};

// =============================================================================
// FUNZIONI PER GESTIONE CANTIERI E COMMITTENTI
// =============================================================================

/**
 * COMMITTENTI - CRUD Operations
 */

// Crea un nuovo committente
export const createCommittente = async (committenteData) => {
  try {
    console.log("createCommittente: Creazione committente:", committenteData);
    
    // Validazione dati obbligatori
    if (!committenteData.ragioneSociale?.trim()) {
      throw new Error("La ragione sociale è obbligatoria");
    }
    
    // Prepara i dati per il salvataggio
    const dataToSave = {
      ragioneSociale: committenteData.ragioneSociale.trim(),
      partitaIva: committenteData.partitaIva?.trim() || '',
      pec: committenteData.pec?.trim() || '',
      referente: committenteData.referente?.trim() || '',
      note: committenteData.note?.trim() || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || 'unknown'
    };
    
    const committentiRef = collection(db, "committenti");
    const docRef = await addDoc(committentiRef, dataToSave);
    
    console.log("createCommittente: Committente creato con ID:", docRef.id);
    
    return {
      id: docRef.id,
      ...dataToSave,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error("createCommittente: Errore nella creazione:", error);
    throw error;
  }
};

// Recupera tutti i committenti
export const getCommittenti = async (filters = {}) => {
  try {
    console.log("getCommittenti: Recupero committenti con filtri:", filters);
    
    const committentiRef = collection(db, "committenti");
    let queryConstraints = [orderBy("ragioneSociale", "asc")];
    
    const q = query(committentiRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    
    let committenti = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    // Applica filtri lato client se necessario
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      committenti = committenti.filter(committente =>
        committente.ragioneSociale.toLowerCase().includes(searchTerm) ||
        (committente.partitaIva && committente.partitaIva.toLowerCase().includes(searchTerm)) ||
        (committente.referente && committente.referente.toLowerCase().includes(searchTerm))
      );
    }
    
    console.log(`getCommittenti: Trovati ${committenti.length} committenti`);
    return committenti;
  } catch (error) {
    console.error("getCommittenti: Errore nel recupero:", error);
    throw error;
  }
};

// Recupera un singolo committente per ID
export const getCommittenteById = async (committenteId) => {
  try {
    console.log("getCommittenteById: Recupero committente ID:", committenteId);
    
    if (!committenteId) throw new Error("ID committente obbligatorio");
    
    const committenteRef = doc(db, "committenti", committenteId);
    const docSnap = await getDoc(committenteRef);
    
    if (!docSnap.exists()) {
      throw new Error("Committente non trovato");
    }
    
    const data = docSnap.data();
    const committente = {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date()
    };
    
    console.log("getCommittenteById: Committente trovato:", committente.ragioneSociale);
    return committente;
  } catch (error) {
    console.error("getCommittenteById: Errore nel recupero:", error);
    throw error;
  }
};

// Aggiorna un committente
export const updateCommittente = async (committenteId, updateData) => {
  try {
    console.log("updateCommittente: Aggiornamento committente ID:", committenteId);
    
    if (!committenteId) throw new Error("ID committente obbligatorio");
    if (!updateData.ragioneSociale?.trim()) {
      throw new Error("La ragione sociale è obbligatoria");
    }
    
    // Prepara i dati per l'aggiornamento
    const dataToUpdate = {
      ragioneSociale: updateData.ragioneSociale.trim(),
      partitaIva: updateData.partitaIva?.trim() || '',
      pec: updateData.pec?.trim() || '',
      referente: updateData.referente?.trim() || '',
      note: updateData.note?.trim() || '',
      updatedAt: serverTimestamp()
    };
    
    const committenteRef = doc(db, "committenti", committenteId);
    await updateDoc(committenteRef, dataToUpdate);
    
    console.log("updateCommittente: Committente aggiornato con successo");
    return {
      id: committenteId,
      ...dataToUpdate,
      updatedAt: new Date()
    };
  } catch (error) {
    console.error("updateCommittente: Errore nell'aggiornamento:", error);
    throw error;
  }
};

// Elimina un committente
export const deleteCommittente = async (committenteId) => {
  try {
    console.log("deleteCommittente: Eliminazione committente ID:", committenteId);
    
    if (!committenteId) throw new Error("ID committente obbligatorio");
    
    // Verifica se ci sono cantieri associati
    const cantieriAssociati = await getCantieriByCommittente(committenteId);
    if (cantieriAssociati.length > 0) {
      throw new Error(`Impossibile eliminare il committente. Ci sono ${cantieriAssociati.length} cantieri associati.`);
    }
    
    const committenteRef = doc(db, "committenti", committenteId);
    await deleteDoc(committenteRef);
    
    console.log("deleteCommittente: Committente eliminato con successo");
    return true;
  } catch (error) {
    console.error("deleteCommittente: Errore nell'eliminazione:", error);
    throw error;
  }
};

// ========================================
// CANTIERI - CRUD Operations (VERSIONE CORRETTA)
// ========================================

/**
 * Crea un nuovo cantiere
 */
export const createCantiere = async (cantiereData) => {
  try {
    console.log("createCantiere: Creazione nuovo cantiere", cantiereData);

    // Validazione responsabili
    if (!cantiereData.responsabiliCantiere || cantiereData.responsabiliCantiere.length === 0) {
      throw new Error("Almeno un responsabile cantiere è obbligatorio");
    }

    // Verifica che tutti i responsabili esistano
    const responsabiliIds = cantiereData.responsabiliCantiere;
    for (const responsabileId of responsabiliIds) {
      const userDoc = await getDoc(doc(db, "users", responsabileId));
      if (!userDoc.exists()) {
        throw new Error(`Responsabile con ID ${responsabileId} non trovato`);
      }
      
      const userData = userDoc.data();
      if (userData.role === 'admin') {
        throw new Error("Gli admin non possono essere assegnati come responsabili (hanno accesso automatico)");
      }
    }

    // Prepara i dati del cantiere
    const cantiereToSave = {
      ...cantiereData,
      responsabiliCantiere: responsabiliIds, // Array di ID
      createdAt: serverTimestamp(), // ✅ CORRETTO: usa serverTimestamp
      updatedAt: serverTimestamp(), // ✅ CORRETTO: usa serverTimestamp
      // Mantieni retrocompatibilità per ora
      responsabileCantiere: responsabiliIds[0] || null
    };

    // Converti le date se presenti
    if (cantiereData.dataInizioLavori) {
      cantiereToSave.dataInizioLavori = new Date(cantiereData.dataInizioLavori);
    }

    if (cantiereData.dataFineLavori) {
      cantiereToSave.dataFineLavori = new Date(cantiereData.dataFineLavori);  
    }

    const cantieriRef = collection(db, "cantieri");
    const docRef = await addDoc(cantieriRef, cantiereToSave);
    
    console.log("createCantiere: Cantiere creato con ID:", docRef.id);
    return docRef.id;
    
  } catch (error) {
    console.error("createCantiere: Errore nella creazione:", error);
    throw error;
  }
};

/**
 * Recupera tutti i cantieri con filtri opzionali
 */
export const getCantieri = async (filters = {}) => {
  try {
    console.log("getCantieri: Recupero cantieri con filtri:", filters);
    
    const cantieriRef = collection(db, "cantieri");
    let queryConstraints = [orderBy("createdAt", "desc")];
    
    // Applica filtri
    if (filters.committenteId) {
      queryConstraints.push(where("committenteId", "==", filters.committenteId));
    }
    
    if (filters.stato) {
      queryConstraints.push(where("stato", "==", filters.stato));
    }
    
    if (filters.responsabileCantiere) {
      queryConstraints.push(where("responsabileCantiere", "==", filters.responsabileCantiere));
    }
    
    const q = query(cantieriRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    
    const cantieri = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        dataInizioLavori: data.dataInizioLavori?.toDate?.() || data.dataInizioLavori,
        dataFineLavori: data.dataFineLavori?.toDate?.() || data.dataFineLavori,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    console.log(`getCantieri: Trovati ${cantieri.length} cantieri`);
    return cantieri;
  } catch (error) {
    console.error("getCantieri: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Recupera cantieri per committente
 */
export const getCantieriByCommittente = async (committenteId) => {
  try {
    console.log("getCantieriByCommittente: Recupero cantieri per committente:", committenteId);
    
    if (!committenteId) throw new Error("ID committente obbligatorio");
    
    return await getCantieri({ committenteId });
  } catch (error) {
    console.error("getCantieriByCommittente: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Recupera un singolo cantiere per ID
 */
export const getCantiereById = async (cantiereId) => {
  try {
    console.log("getCantiereById: Recupero cantiere ID:", cantiereId);
    
    if (!cantiereId) throw new Error("ID cantiere obbligatorio");
    
    const cantiereRef = doc(db, "cantieri", cantiereId);
    const docSnap = await getDoc(cantiereRef);
    
    if (!docSnap.exists()) {
      throw new Error("Cantiere non trovato");
    }
    
    const data = docSnap.data();
    const cantiere = {
      id: docSnap.id,
      ...data,
      dataInizioLavori: data.dataInizioLavori?.toDate?.() || data.dataInizioLavori,
      dataFineLavori: data.dataFineLavori?.toDate?.() || data.dataFineLavori,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date()
    };
    
    console.log("getCantiereById: Cantiere trovato:", cantiere.nomeCommessa);
    return cantiere;
  } catch (error) {
    console.error("getCantiereById: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Aggiorna un cantiere
 */
export const updateCantiere = async (cantiereId, cantiereData) => {
  try {
    console.log("updateCantiere: Aggiornamento cantiere", cantiereId, cantiereData);

    // Validazione responsabili
    if (!cantiereData.responsabiliCantiere || cantiereData.responsabiliCantiere.length === 0) {
      throw new Error("Almeno un responsabile cantiere è obbligatorio");
    }

    // Verifica che tutti i responsabili esistano
    const responsabiliIds = cantiereData.responsabiliCantiere;
    for (const responsabileId of responsabiliIds) {
      const userDoc = await getDoc(doc(db, "users", responsabileId));
      if (!userDoc.exists()) {
        throw new Error(`Responsabile con ID ${responsabileId} non trovato`);
      }
      
      const userData = userDoc.data();
      if (userData.role === 'admin') {
        throw new Error("Gli admin non possono essere assegnati come responsabili (hanno accesso automatico)");
      }
    }

    // Prepara i dati da aggiornare
    const updateData = {
      ...cantiereData,
      responsabiliCantiere: responsabiliIds, // Array di ID
      updatedAt: serverTimestamp(), // ✅ CORRETTO: usa serverTimestamp
      // Mantieni retrocompatibilità per ora
      responsabileCantiere: responsabiliIds[0] || null
    };

    // Converti le date se presenti
    if (cantiereData.dataInizioLavori) {
      updateData.dataInizioLavori = new Date(cantiereData.dataInizioLavori);
    }

    if (cantiereData.dataFineLavori) {
      updateData.dataFineLavori = new Date(cantiereData.dataFineLavori);
    }

    const cantiereRef = doc(db, "cantieri", cantiereId);
    await updateDoc(cantiereRef, updateData);
    
    console.log("updateCantiere: Cantiere aggiornato con successo");
    return true;
    
  } catch (error) {
    console.error("updateCantiere: Errore nell'aggiornamento:", error);
    throw error;
  }
};

/**
 * Archivia un cantiere (cambia stato)
 */
export const archiveCantiere = async (cantiereId, archivia = true) => {
  try {
    console.log(`${archivia ? 'Archiviazione' : 'Ripristino'} cantiere ID:`, cantiereId);
    
    const nuovoStato = archivia ? 'Archiviato' : 'Attivo';
    
    const cantiereRef = doc(db, "cantieri", cantiereId);
    await updateDoc(cantiereRef, {
      stato: nuovoStato,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Cantiere ${archivia ? 'archiviato' : 'ripristinato'} con successo`);
    return true;
  } catch (error) {
    console.error(`Errore nell'${archivia ? 'archiviazione' : 'ripristino'} del cantiere:`, error);
    throw error;
  }
};

/**
 * Elimina un cantiere (solo se non ha rapportini)
 */
export const deleteCantiere = async (cantiereId) => {
  try {
    console.log("deleteCantiere: Eliminazione cantiere ID:", cantiereId);
    
    if (!cantiereId) throw new Error("ID cantiere obbligatorio");
    
    // Verifica se ci sono rapportini associati
    try {
      const rapportini = await getRapportiniByCantiereId(cantiereId);
      if (rapportini.length > 0) {
        throw new Error(`Impossibile eliminare il cantiere. Ci sono ${rapportini.length} rapportini associati.`);
      }
    } catch (rapportiniError) {
      // Se la funzione rapportini non esiste ancora, procedi con l'eliminazione
      console.warn("Controllo rapportini non disponibile:", rapportiniError.message);
    }
    
    const cantiereRef = doc(db, "cantieri", cantiereId);
    await deleteDoc(cantiereRef);
    
    console.log("deleteCantiere: Cantiere eliminato con successo");
    return true;
  } catch (error) {
    console.error("deleteCantiere: Errore nell'eliminazione:", error);
    throw error;
  }
};

// ========================================
// UTILITY FUNCTIONS per Cantieri
// ========================================

/**
 * Ottieni lista responsabili per dropdown
 */
export const getResponsabiliCantiere = async () => {
  try {
    console.log("getResponsabiliCantiere: Recupero lista responsabili (esclusi admin)");
    
    const usersRef = collection(db, "users");
    
    // Query solo per responsabili (escludiamo admin)
    const responsabiliQuery = query(
      usersRef, 
      where("role", "==", "responsabile")
    );
    
    const responsabiliSnapshot = await getDocs(responsabiliQuery);
    
    const responsabili = [];
    
    // Aggiungi solo responsabili
    responsabiliSnapshot.docs.forEach(doc => {
      const data = doc.data();
      responsabili.push({
        id: doc.id,
        nome: data.nome || '',
        cognome: data.cognome || '',
        email: data.email || '',
        role: data.role || 'responsabile',
        displayName: data.nome && data.cognome ? 
          `${data.nome} ${data.cognome}` : 
          data.email
      });
    });
    
    console.log(`getResponsabiliCantiere: Trovati ${responsabili.length} responsabili`);
    return responsabili;
    
  } catch (error) {
    console.error("getResponsabiliCantiere: Errore nel recupero responsabili:", error);
    throw error;
  }
};

/**
 * Ottieni cantieri per responsabile specifico
 */
export const getCantieriByResponsabile = async (responsabileId) => {
  try {
    console.log("getCantieriByResponsabile: Recupero cantieri per responsabile:", responsabileId);
    
    if (!responsabileId) throw new Error("ID responsabile obbligatorio");
    
    return await getCantieri({ responsabileCantiere: responsabileId });
  } catch (error) {
    console.error("getCantieriByResponsabile: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Ottieni cantieri assegnati a un responsabile specifico
 * Utilizza array-contains per cercare nell'array responsabiliCantiere
 */
export const getCantieriAssegnatiAResponsabile = async (responsabileId) => {
  try {
    console.log("getCantieriAssegnatiAResponsabile: Recupero cantieri per responsabile:", responsabileId);
    
    if (!responsabileId) throw new Error("ID responsabile obbligatorio");
    
    const cantieriRef = collection(db, "cantieri");
    const q = query(
      cantieriRef,
      where("responsabiliCantiere", "array-contains", responsabileId),
      where("stato", "==", "Attivo"), // Solo cantieri attivi
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    
    const cantieri = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        dataInizioLavori: data.dataInizioLavori?.toDate?.() || data.dataInizioLavori,
        dataFineLavori: data.dataFineLavori?.toDate?.() || data.dataFineLavori,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    console.log(`getCantieriAssegnatiAResponsabile: Trovati ${cantieri.length} cantieri attivi`);
    return cantieri;
    
  } catch (error) {
    console.error("getCantieriAssegnatiAResponsabile: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Statistiche cantieri per dashboard
 */
export const getCantieriStats = async () => {
  try {
    console.log("getCantieriStats: Recupero statistiche cantieri");
    
    const cantieri = await getCantieri();
    
    const stats = {
      totale: cantieri.length,
      attivi: cantieri.filter(c => c.stato === 'Attivo').length,
      archiviati: cantieri.filter(c => c.stato === 'Archiviato').length,
      perImpresa: {
        Isap: cantieri.filter(c => c.impresa === 'Isap').length,
        Fd: cantieri.filter(c => c.impresa === 'Fd').length,
        StradeDg: cantieri.filter(c => c.impresa === 'StradeDg').length
      }
    };
    
    console.log("getCantieriStats: Statistiche calcolate:", stats);
    return stats;
  } catch (error) {
    console.error("getCantieriStats: Errore nel calcolo statistiche:", error);
    throw error;
  }
};

/**
 * Funzione per verificare se un utente può accedere a un cantiere
 */
export const canUserAccessCantiere = async (userId, cantiereId) => {
  try {
    // Ottieni dati utente
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    
    // Admin ha accesso a tutti i cantieri
    if (userData.role === 'admin') {
      return true;
    }
    
    // Ottieni dati cantiere
    const cantiereDoc = await getDoc(doc(db, "cantieri", cantiereId));
    if (!cantiereDoc.exists()) {
      return false;
    }
    
    const cantiereData = cantiereDoc.data();
    
    // Verifica se l'utente è tra i responsabili
    const responsabiliCantiere = cantiereData.responsabiliCantiere || [];
    
    // Retrocompatibilità: controlla anche il campo singolo
    const responsabileCantiere = cantiereData.responsabileCantiere;
    
    return responsabiliCantiere.includes(userId) || 
           (responsabileCantiere && responsabileCantiere === userId);
    
  } catch (error) {
    console.error("canUserAccessCantiere: Errore nella verifica accesso:", error);
    return false;
  }
};

// ========================================
// RAPPORTINI - CRUD Operations
// ========================================

/**
 * Crea un nuovo rapportino
 */
export const createRapportino = async (rapportinoData) => {
  try {
    console.log("createRapportino: Creazione nuovo rapportino", rapportinoData);

    // Validazioni base
    if (!rapportinoData.cantiereId) {
      throw new Error("ID cantiere è obbligatorio");
    }
    
    if (!rapportinoData.data) {
      throw new Error("Data rapportino è obbligatoria");
    }

    // ✅ CORREZIONE: Validazioni meno restrittive per permettere rapportini vuoti iniziali
    // if (!rapportinoData.personalePresente || rapportinoData.personalePresente.length === 0) {
    //   throw new Error("Almeno una persona presente è obbligatoria");
    // }

    // Verifica che non esista già un rapportino per quella data
    const existingRapportino = await getRapportinoByCantiereAndDate(
      rapportinoData.cantiereId, 
      rapportinoData.data
    );
    
    if (existingRapportino) {
      throw new Error(`Esiste già un rapportino per il ${new Date(rapportinoData.data).toLocaleDateString('it-IT')}`);
    }

    // Calcola i totali
    const totali = calculateRapportinoTotals(rapportinoData);

    // Prepara i dati del rapportino
    const rapportinoToSave = {
      ...rapportinoData,
      ...totali,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      stato: 'Attivo',
      modifiche: [] // Array per tenere traccia delle modifiche
    };

    // Converti la data se è una stringa
    if (typeof rapportinoData.data === 'string') {
      rapportinoToSave.data = new Date(rapportinoData.data);
    }

    const rapportiniRef = collection(db, "rapportini");
    const docRef = await addDoc(rapportiniRef, rapportinoToSave);
    
    console.log("createRapportino: Rapportino creato con ID:", docRef.id);
    return docRef.id;
    
  } catch (error) {
    console.error("createRapportino: Errore nella creazione:", error);
    throw error;
  }
};

/**
 * Aggiorna un rapportino esistente
 */
export const updateRapportino = async (rapportinoId, rapportinoData) => {
  try {
    console.log("updateRapportino: Aggiornamento rapportino ID:", rapportinoId);
    
    if (!rapportinoId) throw new Error("ID rapportino è obbligatorio");

    // Calcola i totali aggiornati
    const totali = calculateRapportinoTotals(rapportinoData);

    const rapportinoToUpdate = {
      ...rapportinoData,
      ...totali,
      updatedAt: serverTimestamp()
    };

    // Converti la data se è una stringa
    if (typeof rapportinoData.data === 'string') {
      rapportinoToUpdate.data = new Date(rapportinoData.data);
    }

    const rapportinoRef = doc(db, "rapportini", rapportinoId);
    await updateDoc(rapportinoRef, rapportinoToUpdate);
    
    console.log("updateRapportino: Rapportino aggiornato con successo");
    return true;
  } catch (error) {
    console.error("updateRapportino: Errore nell'aggiornamento:", error);
    throw error;
  }
};

/**
 * Ottieni tutti i rapportini di un cantiere
 */
export const getRapportiniByCantiereId = async (cantiereId) => {
  try {
    console.log("getRapportiniByCantiereId: Recupero rapportini per cantiere:", cantiereId);
    
    if (!cantiereId) throw new Error("ID cantiere è obbligatorio");

    const rapportiniRef = collection(db, "rapportini");
    const q = query(
      rapportiniRef,
      where("cantiereId", "==", cantiereId),
      where("stato", "==", "Attivo"),
      orderBy("data", "desc")
    );

    const querySnapshot = await getDocs(q);
    const rapportini = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      rapportini.push({
        id: doc.id,
        ...data,
        // Converti Timestamp di Firestore in Date per la visualizzazione
        data: data.data?.toDate ? data.data.toDate() : new Date(data.data),
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
      });
    });

    console.log(`getRapportiniByCantiereId: Trovati ${rapportini.length} rapportini`);
    return rapportini;
    
  } catch (error) {
    console.error("getRapportiniByCantiereId: Errore nel recupero:", error);
    throw error;
  }
};

/**
 * Ottieni un singolo rapportino per cantiere e data
 */
export const getRapportinoByCantiereAndDate = async (cantiereId, data) => {
  try {
    console.log("getRapportinoByCantiereAndDate: Ricerca rapportino per cantiere:", cantiereId, "data:", data);
    
    const rapportiniRef = collection(db, "rapportini");
    const searchDate = new Date(data);
    
    const q = query(
      rapportiniRef,
      where("cantiereId", "==", cantiereId),
      where("data", "==", searchDate),
      where("stato", "==", "Attivo")
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const rapportinoData = doc.data();
    
    return {
      id: doc.id,
      ...rapportinoData,
      data: rapportinoData.data?.toDate ? rapportinoData.data.toDate() : new Date(rapportinoData.data),
      createdAt: rapportinoData.createdAt?.toDate ? rapportinoData.createdAt.toDate() : rapportinoData.createdAt,
      updatedAt: rapportinoData.updatedAt?.toDate ? rapportinoData.updatedAt.toDate() : rapportinoData.updatedAt
    };
    
  } catch (error) {
    console.error("getRapportinoByCantiereAndDate: Errore nella ricerca:", error);
    throw error;
  }
};

/**
 * Duplica un rapportino con sincronizzazione automatica
 */
export const duplicateRapportino = async (rapportinoId, cantiereId, targetDate = null) => {
  try {
    console.log("🔄 duplicateRapportino: Inizio duplicazione rapportino ID:", rapportinoId);
    
    if (!rapportinoId) throw new Error("ID rapportino è obbligatorio");

    // Recupera il rapportino originale
    const rapportinoRef = doc(db, "rapportini", rapportinoId);
    const rapportinoDoc = await getDoc(rapportinoRef);
    
    if (!rapportinoDoc.exists()) {
      throw new Error("Rapportino non trovato");
    }

    const originalData = rapportinoDoc.data();
    
    // USA LA DATA FORNITA O QUELLA ODIERNA
    const dataRapportino = targetDate || new Date().toISOString().split('T')[0];
    
    console.log("📄 Duplicazione rapportino:", {
      originale: originalData.data,
      nuovaData: dataRapportino,
      personale: originalData.personalePresente?.length || 0
    });

    // Valida la data confrontando stringhe
    const todayStr = new Date().toISOString().split('T')[0];
    
    if (dataRapportino > todayStr) {
      throw new Error("Non è possibile duplicare un rapportino con data futura");
    }

    // Crea i dati per il nuovo rapportino
    const newRapportinoData = {
      ...originalData,
      data: dataRapportino, // USA LA DATA PERSONALIZZATA
      allegati: [], // Reset degli allegati
      
      // CAMPI PER TRACKING E SINCRONIZZAZIONE
      isDuplicated: true,
      duplicatedFrom: rapportinoId,
      duplicatedAt: serverTimestamp(),
      duplicatedTargetDate: dataRapportino,
      syncStatus: 'pending',
      syncMessage: `In attesa di sincronizzazione ore per ${dataRapportino}...`
    };

    // Rimuovi campi che non devono essere duplicati
    delete newRapportinoData.id;
    delete newRapportinoData.createdAt;
    delete newRapportinoData.updatedAt;
    delete newRapportinoData.modifiche;
    delete newRapportinoData.lastNightlySync;
    delete newRapportinoData.syncCompletedAt;

    // Crea il nuovo rapportino
    const newRapportinoId = await createRapportino(newRapportinoData);
    
    console.log("✅ Rapportino duplicato con ID:", newRapportinoId, "data:", dataRapportino);

    // 🆕 SINCRONIZZAZIONE DIRETTA IN BACKGROUND
    setTimeout(async () => {
      try {
        console.log(`🔄 [Background] Avvio sincronizzazione DIRETTA per ${newRapportinoId}`);
        
        // STEP 1: Aggiorna stato a "syncing"
        const rapportinoRef = doc(db, 'rapportini', newRapportinoId);
        await updateDoc(rapportinoRef, {
          syncStatus: 'syncing',
          syncMessage: `Sincronizzazione ore per ${dataRapportino} in corso...`,
          syncStartedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        console.log(`🔄 [Background] Stato aggiornato a 'syncing'`);
        
        // STEP 2: 🆕 USA SINCRONIZZAZIONE DIRETTA invece di triggerBatchDistribution
        const { syncDuplicatedRapportino } = await import('./services/directRapportinoSync');
        
        console.log(`🔄 [Background] Eseguo syncDuplicatedRapportino per data ${dataRapportino}`);
        
        const syncResult = await syncDuplicatedRapportino(newRapportinoId, dataRapportino);
        
        console.log(`✅ [Background] Sincronizzazione diretta completata:`, syncResult);
        
        // STEP 3: Aggiorna stato finale in base al risultato
        if (syncResult.success) {
          if (syncResult.updated > 0) {
            // Almeno una persona sincronizzata
            await updateDoc(rapportinoRef, {
              syncStatus: 'completed',
              syncMessage: `✅ Sincronizzate ${syncResult.updated} persone su ${syncResult.total} per ${dataRapportino}`,
              syncCompletedAt: serverTimestamp(),
              isDuplicated: false, // Rimuovi flag dopo sync riuscita
              syncResult: {
                successful: syncResult.updated,
                total: syncResult.total,
                notFound: syncResult.notFound,
                targetDate: dataRapportino
              },
              updatedAt: serverTimestamp()
            });
            
            console.log(`✅ [Background] ${syncResult.updated} persone sincronizzate per ${dataRapportino}`);
          } else if (syncResult.notFound === syncResult.total) {
            // Nessuna ora trovata per nessuno
            await updateDoc(rapportinoRef, {
              syncStatus: 'completed',
              syncMessage: `⚠️ Nessuna ora trovata in workHours per ${dataRapportino}`,
              syncCompletedAt: serverTimestamp(),
              isDuplicated: false,
              syncResult: syncResult,
              updatedAt: serverTimestamp()
            });
            
            console.log(`⚠️ [Background] Nessuna ora trovata per ${dataRapportino}`);
          } else {
            // Alcuni sincronizzati, altri no
            await updateDoc(rapportinoRef, {
              syncStatus: 'completed',
              syncMessage: `⚠️ Sincronizzate ${syncResult.updated} persone, ${syncResult.notFound} senza ore`,
              syncCompletedAt: serverTimestamp(),
              isDuplicated: false,
              syncResult: syncResult,
              updatedAt: serverTimestamp()
            });
            
            console.log(`⚠️ [Background] Sincronizzazione parziale per ${dataRapportino}`);
          }
        } else {
          // Errore nella sincronizzazione
          throw new Error(syncResult.message || 'Errore sincronizzazione');
        }
        
      } catch (error) {
        console.error(`❌ [Background] Errore sincronizzazione per ${dataRapportino}:`, error);
        
        try {
          // Aggiorna stato a "error"
          const rapportinoRef = doc(db, 'rapportini', newRapportinoId);
          await updateDoc(rapportinoRef, {
            syncStatus: 'error',
            syncMessage: `⚠️ Errore sincronizzazione per ${dataRapportino} - verrà riprovata stanotte`,
            syncError: error.message,
            syncErrorTimestamp: serverTimestamp(),
            // NON rimuovere isDuplicated in caso di errore (per job notturno)
            updatedAt: serverTimestamp()
          });
        } catch (updateError) {
          console.error('❌ [Background] Errore aggiornamento stato errore:', updateError);
        }
      }
    }, 2000); // Delay di 2 secondi
    
    // Ritorna immediatamente l'ID
    return newRapportinoId;
    
  } catch (error) {
    console.error("❌ duplicateRapportino: Errore nella duplicazione:", error);
    throw error;
  }
};

/**
 * Elimina un rapportino (soft delete)
 */
export const deleteRapportino = async (rapportinoId) => {
  try {
    console.log("deleteRapportino: Eliminazione rapportino ID:", rapportinoId);
    
    if (!rapportinoId) throw new Error("ID rapportino è obbligatorio");

    // Soft delete - cambia solo lo stato
    const rapportinoRef = doc(db, "rapportini", rapportinoId);
    await updateDoc(rapportinoRef, {
      stato: 'Eliminato',
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log("deleteRapportino: Rapportino eliminato (soft delete) con successo");
    return true;
  } catch (error) {
    console.error("deleteRapportino: Errore nell'eliminazione:", error);
    throw error;
  }
};

/**
 * Elimina un rapportino officina (soft delete)
 */
export const deleteRapportinoOfficina = async (rapportinoId) => {
  try {
    const rapportinoRef = doc(db, "rapportiniOfficina", rapportinoId);
    await deleteDoc(rapportinoRef); // ✅ Cancellazione fisica
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload file per rapportini con validazioni complete
 */
export const uploadRapportinoFile = async (file, cantiereId, fileName) => {
  try {
    console.log("🔄 uploadRapportinoFile: Inizio upload", {
      cantiereId,
      fileName,
      fileType: file.type,
      fileSize: file.size
    });
    
    // ✅ VALIDAZIONI PRELIMINARI
    if (!file) throw new Error("File è obbligatorio");
    if (!cantiereId) throw new Error("ID cantiere è obbligatorio");
    if (!fileName) throw new Error("Nome file è obbligatorio");

    // ✅ VALIDAZIONE TIPO FILE
    const allowedTypes = [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/gif', 
      'image/webp',
      'pdf'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        `Tipo file non supportato: ${file.type}. ` +
        `Accettati: ${allowedTypes.join(', ')}`
      );
    }

    // ✅ VALIDAZIONE DIMENSIONE
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error(
        `File troppo grande: ${(file.size / 1024 / 1024).toFixed(2)}MB. ` +
        `Massimo consentito: 10MB`
      );
    }

    // ✅ CREAZIONE RIFERIMENTO STORAGE
    const storageReference = storageRef(
      firebaseStorage, 
      `rapportini/${cantiereId}/${fileName}`
    );
    
    console.log("📍 Percorso storage:", `rapportini/${cantiereId}/${fileName}`);
    
    // ✅ UPLOAD EFFETTIVO
    console.log("📤 Inizio upload fisico...");
    const uploadResult = await storageUploadBytes(storageReference, file);
    console.log("✅ Upload completato:", uploadResult);
    
    // ✅ OTTIENI URL DOWNLOAD
    console.log("🔗 Recupero URL download...");
    const fileDownloadURL = await storageGetDownloadURL(uploadResult.ref);
    console.log("✅ URL generato:", fileDownloadURL);
    
    return fileDownloadURL;
    
  } catch (error) {
    console.error("❌ uploadRapportinoFile: Errore:", error);
    
    // ✅ ERRORI SPECIFICI PER DEBUG
    if (error.code === 'storage/unauthorized') {
      throw new Error("Permessi insufficienti per l'upload. Verifica le regole Firebase.");
    } else if (error.code === 'storage/quota-exceeded') {
      throw new Error("Quota storage superata. Contatta l'amministratore.");
    } else if (error.code === 'storage/invalid-format') {
      throw new Error("Formato file non valido.");
    }
    
    throw error;
  }
};

/**
 * Elimina file da storage (opzionale)
 */
export const deleteRapportinoFile = async (fileURL) => {
  try {
    const fileRef = storageRef(firebaseStorage, fileURL);
    await storageDeleteObject(fileRef);
    console.log("🗑️ File eliminato da storage:", fileURL);
  } catch (error) {
    console.error("❌ Errore eliminazione file:", error);
    throw error;
  }
};

/**
 * Calcola i totali di un rapportino
 */
export const calculateRapportinoTotals = (rapportinoData) => {
  try {
    const personale = rapportinoData.personalePresente || [];
    const mezzi = rapportinoData.mezziUtilizzati || [];
    const materialiVariabili = rapportinoData.materialiVariabili || [];
    const materialiFissi = rapportinoData.materialiFissi || [];
    const ricavi = rapportinoData.ricavi || [];
    
    // Calcolo totali con gestione errori per ogni campo
    const totalePersonale = personale.reduce((sum, p) => {
      const costo = parseFloat(p.costoOrario) || 0;
      const ore = parseFloat(p.oreLavorate) || 0;
      return sum + (costo * ore);
    }, 0);
    
    const totaleMezzi = mezzi.reduce((sum, m) => {
      return sum + parseFloat(m.totale || 0);
    }, 0);
    
    const totaleMatVariabili = materialiVariabili.reduce((sum, m) => {
      return sum + parseFloat(m.costo || 0);
    }, 0);
    
    const totaleMatFissi = materialiFissi.reduce((sum, m) => {
      return sum + parseFloat(m.totale || 0);
    }, 0);
    
    const totaleRicavi = ricavi.reduce((sum, r) => {
      return sum + parseFloat(r.importo || 0);
    }, 0);
    
    const totaleCosti = totalePersonale + totaleMezzi + totaleMatVariabili + totaleMatFissi;
    const margine = totaleRicavi - totaleCosti;
    
    return {
      totalePersonale: parseFloat(totalePersonale.toFixed(2)),
      totaleMezzi: parseFloat(totaleMezzi.toFixed(2)),
      totaleMatVariabili: parseFloat(totaleMatVariabili.toFixed(2)),
      totaleMatFissi: parseFloat(totaleMatFissi.toFixed(2)),
      totaleCosti: parseFloat(totaleCosti.toFixed(2)),
      totaleRicavi: parseFloat(totaleRicavi.toFixed(2)),
      margine: parseFloat(margine.toFixed(2))
    };
  } catch (error) {
    console.error('Errore nel calcolo totali rapportino:', error);
    return {
      totalePersonale: 0,
      totaleMezzi: 0,
      totaleMatVariabili: 0,
      totaleMatFissi: 0,
      totaleCosti: 0,
      totaleRicavi: 0,
      margine: 0
    };
  }
};

/**
 * Verifica se l'utente può gestire il rapportino (modificare/eliminare)
 */
export const canUserManageRapportino = async (userId, rapportinoId) => {
  try {
    // Recupera i dati dell'utente
    const userData = await getUserData(userId);
    if (!userData) return false;
    
    // Gli admin possono gestire tutti i rapportini
    if (userData.role === 'admin') return true;
    
    // Recupera i dati del rapportino
    const rapportinoRef = doc(db, 'rapportini', rapportinoId);
    const rapportinoSnap = await getDoc(rapportinoRef);
    
    if (!rapportinoSnap.exists()) return false;
    
    const rapportinoData = rapportinoSnap.data();
    
    // L'utente può gestire i propri rapportini
    if (rapportinoData.redattoDa === userId) return true;
    
    // Verifica se l'utente può accedere al cantiere del rapportino
    return await canUserAccessCantiere(userId, rapportinoData.cantiereId);
  } catch (error) {
    console.error('Errore nella verifica gestione rapportino:', error);
    return false;
  }
};

/**
 * Ottieni galleria immagini per cantiere
 */
export const getGalleriaImmagini = async (cantiereId, dateFrom = null, dateTo = null) => {
  try {
    console.log("getGalleriaImmagini: Recupero galleria per cantiere:", cantiereId);
    
    const rapportini = await getRapportiniByCantiereId(cantiereId);
    
    const galleria = [];
    
    rapportini.forEach(rapportino => {
      if (rapportino.allegati && rapportino.allegati.length > 0) {
        rapportino.allegati.forEach(allegato => {
          // Filtra solo le immagini
          if (allegato.type && allegato.type.startsWith('image/')) {
            galleria.push({
              id: `${rapportino.id}_${allegato.name}`, // ID univoco
              nome: allegato.name,
              url: allegato.url,
              thumbnailUrl: allegato.thumbnailUrl || allegato.url,
              type: allegato.type,
              size: allegato.size,
              descrizione: allegato.descrizione || '',
              dataCaricamento: rapportino.data,
              dataRapportino: rapportino.data,
              nomeRapportino: rapportino.data,
              rapportinoId: rapportino.id
            });
          }
        });
      }
    });

    // Ordina per data (più recenti prima)
    galleria.sort((a, b) => new Date(b.dataCaricamento) - new Date(a.dataCaricamento));
    
    console.log(`getGalleriaImmagini: Trovate ${galleria.length} immagini`);
    return galleria;
    
  } catch (error) {
    console.error("getGalleriaImmagini: Errore nel recupero galleria:", error);
    return []; // Ritorna array vuoto invece di lanciare errore
  }
};

// ========================================
// FUNZIONI PER GESTIONE MEZZI E ATTREZZATURE
// ========================================

/**
 * Recupera tutti i mezzi (collezione "mezzi")
 */
export const getMezziAttrezzature = async () => {
  try {
    console.log('🚛 Recupero mezzi dalla collezione "mezzi"');
    const mezziRef = collection(db, 'mezzi');
    
    // Rimuovi orderBy se causa problemi, ordina manualmente
    const querySnapshot = await getDocs(mezziRef);
    
    const mezzi = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // ✅ MAPPA il campo corretto: nomeMezzo -> nome
        nome: data.nomeMezzo || data.nome || `Mezzo ${doc.id}`,
        // Mantieni anche i campi originali per compatibilità
        nomeMezzo: data.nomeMezzo,
        costoOrario: data.costoOrario || 0,
        targa: data.targa || '',
        proprieta: data.proprieta || '',
        note: data.note || '',
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    // Ordina manualmente per nome
    mezzi.sort((a, b) => a.nome.localeCompare(b.nome));
    
    console.log(`✅ Trovati ${mezzi.length} mezzi:`, mezzi.map(m => m.nome));
    return mezzi;
  } catch (error) {
    console.error('❌ Errore nel recupero mezzi:', error);
    return [];
  }
};


/**
 * Crea un nuovo mezzo/attrezzatura
 */
export const createMezzoAttrezzatura = async (mezzoData) => {
  try {
    const mezziRef = collection(db, 'mezzi');
    
    // Assicurati che usi i campi corretti
    const dataToSave = {
      nomeMezzo: mezzoData.nomeMezzo || mezzoData.nome || '',
      costoOrario: parseFloat(mezzoData.costoOrario) || 0,
      targa: mezzoData.targa || '',
      proprieta: mezzoData.proprieta || '',
      note: mezzoData.note || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(mezziRef, dataToSave);
    console.log('✅ Mezzo creato con ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Errore nella creazione mezzo:', error);
    throw error;
  }
};


/**
 * Aggiorna un mezzo/attrezzatura
 */
export const updateMezzoAttrezzatura = async (mezzoId, mezzoData) => {
  try {
    const mezzoRef = doc(db, 'mezzi', mezzoId);
    
    const dataToUpdate = {
      nomeMezzo: mezzoData.nomeMezzo || mezzoData.nome,
      costoOrario: parseFloat(mezzoData.costoOrario) || 0,
      targa: mezzoData.targa || '',
      proprieta: mezzoData.proprieta || '',
      note: mezzoData.note || '',
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(mezzoRef, dataToUpdate);
    console.log('✅ Mezzo aggiornato:', mezzoId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'aggiornamento mezzo:', error);
    throw error;
  }
};

/**
 * Elimina un mezzo/attrezzatura
 */
export const deleteMezzoAttrezzatura = async (mezzoId) => {
  try {
    const mezzoRef = doc(db, 'mezzi', mezzoId);
    await deleteDoc(mezzoRef);
    console.log('✅ Mezzo eliminato:', mezzoId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'eliminazione mezzo:', error);
    throw error;
  }
};

// ========================================
// FUNZIONI PER GESTIONE MATERIALI COSTO VARIABILE
// ========================================

/**
 * Recupera tutti i materiali a costo variabile
 */
export const getMaterialiCostoVariabile = async () => {
  try {
    console.log('📦 Recupero materiali variabili dalla collezione "materialiVariabili"');
    const materialiRef = collection(db, 'materialiVariabili');
    
    const querySnapshot = await getDocs(materialiRef);
    
    const materiali = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // ✅ MAPPA il campo corretto: nomeMacrovoce -> nome
        nome: data.nomeMacrovoce || data.nome || `Materiale ${doc.id}`,
        // Mantieni anche i campi originali per compatibilità
        nomeMacrovoce: data.nomeMacrovoce,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    // Ordina manualmente per nome
    materiali.sort((a, b) => a.nome.localeCompare(b.nome));
    
    console.log(`✅ Trovati ${materiali.length} materiali variabili:`, materiali.map(m => m.nome));
    return materiali;
  } catch (error) {
    console.error('❌ Errore nel recupero materiali variabili:', error);
    return [];
  }
};

/**
 * Crea un nuovo materiale a costo variabile
 */
export const createMaterialeCostoVariabile = async (materialeData) => {
  try {
    const materialiRef = collection(db, 'materialiVariabili');
    
    const dataToSave = {
      nomeMacrovoce: materialeData.nomeMacrovoce || materialeData.nome || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(materialiRef, dataToSave);
    console.log('✅ Materiale variabile creato con ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Errore nella creazione materiale variabile:', error);
    throw error;
  }
};

/**
 * Aggiorna un materiale a costo variabile
 */
export const updateMaterialeCostoVariabile = async (materialeId, materialeData) => {
  try {
    const materialeRef = doc(db, 'materialiVariabili', materialeId);
    
    const dataToUpdate = {
      nomeMacrovoce: materialeData.nomeMacrovoce || materialeData.nome,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(materialeRef, dataToUpdate);
    console.log('✅ Materiale variabile aggiornato:', materialeId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'aggiornamento materiale variabile:', error);
    throw error;
  }
};


/**
 * Elimina un materiale a costo variabile
 */
export const deleteMaterialeCostoVariabile = async (materialeId) => {
  try {
    const materialeRef = doc(db, 'materialiVariabili', materialeId);
    await deleteDoc(materialeRef);
    console.log('✅ Materiale variabile eliminato:', materialeId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'eliminazione materiale variabile:', error);
    throw error;
  }
};

// ========================================
// FUNZIONI PER GESTIONE MATERIALI COSTO FISSO
// ========================================

/**
 * Recupera tutti i materiali a costo fisso
 */
export const getMaterialiCostoFisso = async () => {
  try {
    console.log('🏗️ Recupero materiali fissi dalla collezione "materialiFissi"');
    const materialiRef = collection(db, 'materialiFissi');
    
    const querySnapshot = await getDocs(materialiRef);
    
    const materiali = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // ✅ MAPPA il campo corretto: nomeMateriale -> nome
        nome: data.nomeMateriale || data.nome || `Materiale ${doc.id}`,
        // Mantieni anche i campi originali per compatibilità
        nomeMateriale: data.nomeMateriale,
        prezzoUnitario: data.prezzoUnitario || 0,
        unitaMisura: data.unitaMisura || '',
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    });
    
    // Ordina manualmente per nome
    materiali.sort((a, b) => a.nome.localeCompare(b.nome));
    
    console.log(`✅ Trovati ${materiali.length} materiali fissi:`, materiali.map(m => m.nome));
    return materiali;
  } catch (error) {
    console.error('❌ Errore nel recupero materiali fissi:', error);
    return [];
  }
};



/**
 * Crea un nuovo materiale a costo fisso
 */
export const createMaterialeCostoFisso = async (materialeData) => {
  try {
    const materialiRef = collection(db, 'materialiFissi');
    
    const dataToSave = {
      nomeMateriale: materialeData.nomeMateriale || materialeData.nome || '',
      prezzoUnitario: parseFloat(materialeData.prezzoUnitario) || 0,
      unitaMisura: materialeData.unitaMisura || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(materialiRef, dataToSave);
    console.log('✅ Materiale fisso creato con ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Errore nella creazione materiale fisso:', error);
    throw error;
  }
};

/**
 * Aggiorna un materiale a costo fisso
 */
export const updateMaterialeCostoFisso = async (materialeId, materialeData) => {
  try {
    const materialeRef = doc(db, 'materialiFissi', materialeId);
    
    const dataToUpdate = {
      nomeMateriale: materialeData.nomeMateriale || materialeData.nome,
      prezzoUnitario: parseFloat(materialeData.prezzoUnitario) || 0,
      unitaMisura: materialeData.unitaMisura || '',
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(materialeRef, dataToUpdate);
    console.log('✅ Materiale fisso aggiornato:', materialeId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'aggiornamento materiale fisso:', error);
    throw error;
  }
};


/**
 * Elimina un materiale a costo fisso
 */
export const deleteMaterialeCostoFisso = async (materialeId) => {
  try {
    const materialeRef = doc(db, 'materialiFissi', materialeId);
    await deleteDoc(materialeRef);
    console.log('✅ Materiale fisso eliminato:', materialeId);
    return true;
  } catch (error) {
    console.error('❌ Errore nell\'eliminazione materiale fisso:', error);
    throw error;
  }
};

// ========================================
// FUNZIONI PER GESTIONE LAVORAZIONI
// ========================================

/**
 * Recupera tutte le lavorazioni
 */
export const getLavorazioni = async () => {
  try {
    console.log('⚒️ Recupero lavorazioni dalla collezione "lavorazioni"');
    const lavorazioniRef = collection(db, 'lavorazioni'); // ✅ CORRETTO: "lavorazioni"
    const q = query(lavorazioniRef, orderBy('nome', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const lavorazioni = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
    }));
    
    console.log(`✅ Trovate ${lavorazioni.length} lavorazioni`);
    return lavorazioni;
  } catch (error) {
    console.error('❌ Errore nel recupero lavorazioni:', error);
    return [];
  }
};

/**
 * Crea una nuova lavorazione
 */
export const createLavorazione = async (lavorazioneData) => {
  try {
    const lavorazioniRef = collection(db, 'lavorazioni'); // ✅ CORRETTO
    const docRef = await addDoc(lavorazioniRef, {
      ...lavorazioneData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Errore nella creazione lavorazione:', error);
    throw error;
  }
};

/**
 * Aggiorna una lavorazione
 */
export const updateLavorazione = async (lavorazioneId, lavorazioneData) => {
  try {
    const lavorazioneRef = doc(db, 'lavorazioni', lavorazioneId); // ✅ CORRETTO
    await updateDoc(lavorazioneRef, {
      ...lavorazioneData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento lavorazione:', error);
    throw error;
  }
};

/**
 * Elimina una lavorazione
 */
export const deleteLavorazione = async (lavorazioneId) => {
  try {
    const lavorazioneRef = doc(db, 'lavorazioni', lavorazioneId); // ✅ CORRETTO
    await deleteDoc(lavorazioneRef);
    return true;
  } catch (error) {
    console.error('Errore nell\'eliminazione lavorazione:', error);
    throw error;
  }
};

/**
 * Mostra i nomi
 */
export const getUserDisplayName = async (userId) => {
  try {
    if (!userId) return 'Utente sconosciuto';
    
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.nome && userData.cognome 
        ? `${userData.nome} ${userData.cognome}`
        : userData.email || 'Utente sconosciuto';
    }
    return 'Utente sconosciuto';
  } catch (error) {
    console.error('Errore nel recupero nome utente:', error);
    return 'Errore caricamento utente';
  }
};

// ======================================================================
// 1. AGGIORNAMENTO FUNZIONE FIREBASE per recuperare tutti gli allegati
// ======================================================================

// Aggiungi questa funzione aggiornata al file src/firebase.js

/**
 * Ottieni tutti gli allegati (immagini e file) per cantiere
 */
export const getGalleriaFiles = async (cantiereId, fileType = 'all') => {
  try {
    console.log("🔍 getGalleriaFiles: Recupero allegati per cantiere:", cantiereId);
    
    const rapportini = await getRapportiniByCantiereId(cantiereId);
    
    const files = [];
    
    rapportini.forEach(rapportino => {
      if (rapportino.allegati && rapportino.allegati.length > 0) {
        rapportino.allegati.forEach(allegato => {
          // Controllo tipo file
          const isImage = allegato.tipo?.startsWith('immagine');
          const isPDF = allegato.tipo === 'pdf';
          
          // Filtra per tipo se specificato
          if (fileType === 'images' && !isImage) return;
          if (fileType === 'pdf' && !isPDF) return;
          
          files.push({
            id: `${rapportino.id}_${allegato.nomeFile || allegato.nome}`,
            nome: allegato.nome,
            nomeFile: allegato.nomeFile || allegato.nome,
            url: allegato.url,
            tipo: allegato.tipo,
            dimensione: allegato.dimensione,
            descrizione: allegato.descrizione || '',
            dataCaricamento: allegato.uploadedAt || rapportino.createdAt,
            dataRapportino: rapportino.data,
            rapportinoId: rapportino.id,
            redattoDa: rapportino.redattoDa,
            isImage: isImage,
            isPDF: isPDF,
            // Metadati aggiuntivi
            cantiereId: rapportino.cantiereId,
            uploadedBy: allegato.uploadedBy
          });
        });
      }
    });
    
    // Ordina per data di caricamento (più recenti prima)
    files.sort((a, b) => {
      const dateA = new Date(a.dataCaricamento);
      const dateB = new Date(b.dataCaricamento);
      return dateB - dateA;
    });
    
    console.log(`✅ Trovati ${files.length} file per cantiere ${cantiereId}`);
    return files;
    
  } catch (error) {
    console.error("❌ getGalleriaFiles: Errore nel recupero:", error);
    throw error;
  }
};



